"use client"

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Fragment,
  type ChangeEvent,
} from "react"
import dynamic from "next/dynamic"
import { useAuth } from "@clerk/nextjs"
import { useTheme } from "@/components/theme-provider"
import Link from "next/link"
import Image from "next/image"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { 
  ArrowLeft, Calendar, MapPin, Users, Clock, 
  Share2, Edit, Trash2, Copy, CheckCircle,
  Globe, Lock, EyeOff, AlertTriangle, Upload, Loader2, Plus, Radio, Archive,
  BookOpen,
  ExternalLink, X,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { RecurringGuideTrigger } from "@/components/ui/recurring-guide"
import { ModalBody, ModalContent, ModalOverlay } from "@/components/ui/modal-shell"
import { cn } from "@/lib/utils"
import { useHomeHref } from "@/hooks/use-home-href"
import { useRsvp } from "@/hooks/use-rsvp"
import { useTimeline } from "@/hooks/use-timeline"
import { useUserEventCollection } from "@/hooks/use-user-event-collection"
import {
  HOSTING_EVENT_IDS,
  isHosting,
  publishEvent,
  unpublishEvent,
  deleteEvent,
  registerServerCreatedEvent,
} from "@/lib/timeline-store"
import { deleteMedia } from "@/lib/api/media"
import {
  apiEventDtoToEventData,
  deleteEventViaApi,
  EventDeleteNotOnServerError,
  fetchEventFromApi,
  patchEventViaApi,
  postEventStreamMediaSyncFromApi,
  resolveStoredEventById,
} from "@/lib/data/events"
import {
  EVENT_NAV_FROM_PARAM,
  eventNavBackLabel,
  eventNavFallbackHref,
  parseEventNavFrom,
} from "@/lib/events/event-nav-from"
import { StoryScopeOutlineLink } from "@/components/story/story-scope-outline-link"
import {
  appendGuestPreviewQuery,
  appendShareTokenQuery,
  EVENT_GUEST_PREVIEW_QUERY_KEY,
  EVENT_GUEST_PREVIEW_QUERY_VALUE,
  getEventPublicPath,
} from "@/lib/events/urls"
import { writePendingEventAction } from "@/lib/auth/pending-event-action"
import {
  buildAuthIntentQuery,
  clearSessionAuthReturnPath,
} from "@/lib/auth/safe-internal-redirect"
import type { StoryScopeSource } from "@/lib/story/story-routes"
import {
  clearTimelineSaveReminderDismissSession,
  dismissTimelineSaveReminderSession,
  isTimelineSaveReminderDismissedSession,
  markTimelineSaveReminderPulseConsumedSession,
  shouldTimelineSaveReminderPulseSession,
} from "@/lib/timeline/timeline-save-reminder-session"
import { resolveClientEventShareAbsoluteUrl } from "@/lib/events/event-share-url"
import { buildEventWebSharePayload } from "@/lib/events/event-share-text"
import {
  formatEventDateTimeLine,
  formatWeekdayLongMonthDayYear,
} from "@/lib/format/date"
import { EventCoverHeroStage } from "@/components/events/event-cover-hero-stage"
import { getDisplayAddress } from "@/lib/location/address-model"
import { formatCoverLocationHeroSingleLine } from "@/lib/location/cover-location-format"
import { fetchGeocodeSuggestions } from "@/lib/location/geocode-forward"
import {
  isPublicDemoEventId,
  getPublicDemoEvent,
} from "@/lib/demo/public-demo-event"
import {
  eventCoverSrcHero,
  eventHasPersistedHeroCover,
} from "@/lib/events/event-cover-display"
import { IMAGE_QUALITY_AVATAR } from "@/lib/media/image-presets"
import { eventMediaCreatedAtMs } from "@/lib/events/event-media-created-at"
import {
  appendCacheBustToImageUrl,
  buildCloudflareImageUrl,
  CLOUDFLARE_EVENT_COVER_THUMB_VARIANT,
  CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT,
} from "@/lib/media/cloudflare-urls"
import { isAlbumGalleryPurpose } from "@/lib/events/event-media-purpose"
import { resolveCanContributeEventMedia } from "@/lib/events/event-media-permissions"
import {
  shouldSharedEventShowUploadCta,
  sharedPublicEventThemePreference,
} from "@/lib/events/event-upload-policy"
import type { ApiErrorPayload } from "@/lib/api/api-envelope"
import { mapUploadApiErrorToUserMessage } from "@/lib/api/upload-client-messages"
import { BillingEntitlementBlocked } from "@/components/billing/billing-entitlement-blocked"
import { PlanEntitlementBlockedError } from "@/lib/billing/plan-entitlement-blocked-error"
import {
  ALLOWED_EVENT_COVER_MIME_TYPES,
  validateCoverUploadFile,
} from "@/lib/events/cover-selection"
import { uploadEventCoverImageFormData } from "@/lib/events/event-image-upload-form"
import type {
  EventData,
  EventMediaItem,
  EventParticipantRosterItem,
  PersistedRsvpStatus,
} from "@/lib/events/types"
import { EventRsvpChoiceButtons } from "@/components/events/event-rsvp-choice-buttons"
import { MediaDeletedUndoSnackbar } from "@/components/media-deleted-undo-snackbar"
import { useDeferredMediaDelete } from "@/hooks/use-deferred-media-delete"
import { usePendingEventReplay } from "@/hooks/use-pending-event-replay"
import { MEMORIES_REFRESH_EVENT } from "@/hooks/use-memories"
import { EventDetailShell } from "@/components/shells/event-detail-shell"
import {
  getEventTimeStatusPhase,
  isPastEventByStoredDate,
} from "@/lib/events/event-archive"
import {
  EVENT_VISIBILITY_INVITE_ONLY,
  EVENT_VISIBILITY_LINK_ONLY,
  EVENT_VISIBILITY_PRIVATE,
  formatEventVisibilityLabel,
} from "@/lib/events/visibility"
import { guestRsvpHeroSummary } from "@/features/events/rsvp-status"
import type { EventRsvpSnapshot } from "@/lib/rsvp-api"
import { EVENT_COVER_DETAIL_SHELL_SIZES } from "@/lib/media/image-presets"
import {
  getSupportMethodCopyValue,
  getSupportMethodDisplayValue,
  getSupportMethodOpenUrl,
  type NormalizedEventLink,
  type NormalizedSupportMethod,
  parseEventLink,
  parseSupportMethod,
} from "@/lib/events/external-links"

const tabs = ["Details", "Photos", "Guests", "Settings"] as const

const EventVideoSection = dynamic(
  () => import("@/components/event-video-section").then((m) => m.EventVideoSection),
  { ssr: false },
)

const EventActivitySection = dynamic(
  () => import("@/components/events/event-activity-section").then((m) => m.EventActivitySection),
  { ssr: false },
)

const EventManagersPanel = dynamic(
  () => import("@/components/events/event-managers-panel").then((m) => m.EventManagersPanel),
  { ssr: false },
)

const EventPhotosPanel = dynamic(
  () => import("@/components/events/event-photos-panel").then((m) => m.EventPhotosPanel),
  { ssr: false },
)

const EventMediaAddModal = dynamic(
  () => import("@/components/events/event-media-add-modal").then((m) => m.EventMediaAddModal),
  { ssr: false },
)

const EventLocationCompactCard = dynamic(
  () =>
    import("@/components/location/event-location-compact-card").then(
      (m) => m.EventLocationCompactCard,
    ),
  { ssr: false },
)

/** Dedupe post–sign-in resume upload across React Strict Mode double effects. */
const postAuthResumeUploadPromises = new Map<string, Promise<void>>()

function stripSaveTimelineQueryFromBrowserUrl(
  router: ReturnType<typeof useRouter>,
) {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (!url.searchParams.has("save_timeline")) return
  url.searchParams.delete("save_timeline")
  url.searchParams.delete("auth_intent_ts")
  const qs = url.searchParams.toString()
  const next = `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`
  router.replace(next, { scroll: false })
}

function stripResumeUploadQueryFromBrowserUrl(
  router: ReturnType<typeof useRouter>,
) {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (!url.searchParams.has("resume_upload")) return
  url.searchParams.delete("resume_upload")
  url.searchParams.delete("auth_intent_ts")
  const qs = url.searchParams.toString()
  const next = `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`
  router.replace(next, { scroll: false })
}

function stripAlbumDeepLinkQueryFromBrowserUrl(
  router: ReturnType<typeof useRouter>,
) {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (!url.searchParams.has("tab") && !url.searchParams.has("media")) return
  url.searchParams.delete("tab")
  url.searchParams.delete("media")
  const qs = url.searchParams.toString()
  const next = `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`
  router.replace(next, { scroll: false })
}

export function EventPageClient() {
  const params = useParams()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { userId: clerkUserId, isLoaded: authLoaded } = useAuth()
  const { theme, resolvedTheme, setTheme } = useTheme()
  const themeBeforeGuestViewRef = useRef<string | undefined>(undefined)
  const coverFileInputRef = useRef<HTMLInputElement>(null)
  const [coverUploading, setCoverUploading] = useState(false)
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<typeof tabs[number]>("Details")
  const [linkCopied, setLinkCopied] = useState(false)
  const [copyLinkToastOpen, setCopyLinkToastOpen] = useState(false)
  const copyLinkToastDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [eventPageInlineCopyAck, setEventPageInlineCopyAck] = useState(false)
  const eventPageInlineCopyDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [shareCopied, setShareCopied] = useState(false)
  const [copiedEventLinkId, setCopiedEventLinkId] = useState<string | null>(null)
  const [copiedSupportId, setCopiedSupportId] = useState<string | null>(null)
  const [shareCtlBusy, setShareCtlBusy] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteScope, setDeleteScope] = useState<"default" | "series">("default")
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [archiveBusy, setArchiveBusy] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishShareBar, setPublishShareBar] = useState(false)
  const [event, setEvent] = useState<EventData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [mediaModalOpen, setMediaModalOpen] = useState(false)
  const [mediaNotice, setMediaNotice] = useState<{
    tone: "success" | "error"
    text?: string
    entitlement?: ApiErrorPayload
  } | null>(null)
  const [mediaHighlightId, setMediaHighlightId] = useState<string | null>(null)
  const [activityOpenImageMediaId, setActivityOpenImageMediaId] = useState<
    string | null
  >(null)
  const [deepLinkOpenVideoMediaId, setDeepLinkOpenVideoMediaId] = useState<
    string | null
  >(null)
  const albumDeepLinkHandledKeyRef = useRef<string | null>(null)
  const albumDeepLinkRefreshDoneRef = useRef(false)
  const albumDeepLinkRefreshPendingRef = useRef(false)
  /** Album rows optimistically added after upload — merge until API payload catches up. */
  const pendingUploadedAlbumMediaIdsRef = useRef(new Set<string>())
  const [rsvpNotice, setRsvpNotice] = useState<{
    tone: "success" | "error"
    text: string
  } | null>(null)
  const [timelineSaveBusy, setTimelineSaveBusy] = useState(false)
  const [fallbackMapCoords, setFallbackMapCoords] = useState<{
    lat: number
    lng: number
  } | null>(null)
  const [timelineReminderDismissed, setTimelineReminderDismissed] =
    useState(false)
  const [timelineReminderSoftEnter, setTimelineReminderSoftEnter] =
    useState(false)

  const rawParamId = params?.id
  const eventId =
    typeof rawParamId === "string"
      ? rawParamId.trim()
      : Array.isArray(rawParamId)
        ? String(rawParamId[0] ?? "").trim()
        : ""

  useEffect(() => {
    setTimelineReminderDismissed(
      isTimelineSaveReminderDismissedSession(eventId),
    )
  }, [eventId])

  const dismissCalmTimelineReminder = useCallback(() => {
    dismissTimelineSaveReminderSession(eventId)
    setTimelineReminderDismissed(true)
  }, [eventId])

  useEffect(() => {
    if (!clerkUserId?.trim() || !event) return
    clearSessionAuthReturnPath()
  }, [clerkUserId, event])

  useEffect(() => {
    albumDeepLinkHandledKeyRef.current = null
    albumDeepLinkRefreshDoneRef.current = false
    albumDeepLinkRefreshPendingRef.current = false
  }, [eventId])

  useEffect(() => {
    albumDeepLinkRefreshDoneRef.current = false
    albumDeepLinkRefreshPendingRef.current = false
  }, [clerkUserId])

  const urlShareToken = searchParams.get("share")?.trim() || null
  const guestPreviewRequested =
    searchParams.get(EVENT_GUEST_PREVIEW_QUERY_KEY) ===
    EVENT_GUEST_PREVIEW_QUERY_VALUE
  const saveTimelineIntent = searchParams.get("save_timeline")?.trim() ?? ""
  const resumeUploadIntent = searchParams.get("resume_upload")?.trim() ?? ""
  const {
    saves,
    saveToTimeline,
    removeFromTimeline,
    isSaved: isTimelineSaved,
    isLoaded: timelineLoaded,
    refresh: refreshTimelineSaves,
  } = useTimeline()
  const { refresh: refreshUserEvents } = useUserEventCollection({
    timelineSaves: saves,
    timelineLoaded,
  })
  const isPublicDemo = isPublicDemoEventId(eventId)
  const { href: homeHref, label: homeLabel, ready: homeReady } = useHomeHref()
  const navFrom = parseEventNavFrom(searchParams.get(EVENT_NAV_FROM_PARAM))

  const sharedEventReturnBase = useMemo(() => {
    const path = getEventPublicPath({ id: eventId })
    const withShare = appendShareTokenQuery(path, urlShareToken)
    return guestPreviewRequested
      ? appendGuestPreviewQuery(withShare)
      : withShare
  }, [eventId, guestPreviewRequested, urlShareToken])

  const appendAuthReturnQuery = useCallback((base: string, qs: string) => {
    return `${base}${base.includes("?") ? "&" : "?"}${qs}`
  }, [])

  const sharedEventSignInPhotos = useMemo(() => {
    return appendAuthReturnQuery(
      sharedEventReturnBase,
      buildAuthIntentQuery({ tab: "photos" }),
    )
  }, [appendAuthReturnQuery, sharedEventReturnBase])

  const sharedEventSignInPhotosUpload = useMemo(() => {
    return appendAuthReturnQuery(
      sharedEventReturnBase,
      buildAuthIntentQuery({ tab: "photos", resume_upload: "1" }),
    )
  }, [appendAuthReturnQuery, sharedEventReturnBase])

  const sharedEventSignInGuests = useMemo(() => {
    return appendAuthReturnQuery(
      sharedEventReturnBase,
      buildAuthIntentQuery({ tab: "guests" }),
    )
  }, [appendAuthReturnQuery, sharedEventReturnBase])

  const signInHrefForReturnPath = useCallback((returnPath: string) => {
    return `/sign-in?redirect_url=${encodeURIComponent(returnPath)}`
  }, [])

  const beginAuthForGuestTimeline = useCallback(() => {
    const returnPath = appendAuthReturnQuery(
      sharedEventReturnBase,
      buildAuthIntentQuery({}),
    )
    try {
      writePendingEventAction({
        kind: "timeline_save",
        eventId,
        returnPathRaw: returnPath,
        shareToken: urlShareToken,
      })
    } catch {
      /* storage blocked — still send user through auth */
    }
    router.push(`/sign-in?redirect_url=${encodeURIComponent(returnPath)}`)
  }, [
    appendAuthReturnQuery,
    eventId,
    router,
    sharedEventReturnBase,
    urlShareToken,
  ])

  const beginAuthForGuestRsvp = useCallback(
    (status: PersistedRsvpStatus) => {
      const returnPath = appendAuthReturnQuery(
        sharedEventReturnBase,
        buildAuthIntentQuery({}),
      )
      try {
        writePendingEventAction({
          kind: "event_rsvp",
          eventId,
          returnPathRaw: returnPath,
          shareToken: urlShareToken,
          rsvpStatus: status,
        })
      } catch {
        /* ignore */
      }
      router.push(`/sign-in?redirect_url=${encodeURIComponent(returnPath)}`)
    },
    [
      appendAuthReturnQuery,
      eventId,
      router,
      sharedEventReturnBase,
      urlShareToken,
    ],
  )

  const contributorPhotoSignInHref = useMemo(
    () => signInHrefForReturnPath(sharedEventSignInPhotos),
    [signInHrefForReturnPath, sharedEventSignInPhotos],
  )
  const contributorPhotoUploadSignInHref = useMemo(
    () => signInHrefForReturnPath(sharedEventSignInPhotosUpload),
    [signInHrefForReturnPath, sharedEventSignInPhotosUpload],
  )
  const contributorGuestSignInHref = useMemo(
    () => signInHrefForReturnPath(sharedEventSignInGuests),
    [signInHrefForReturnPath, sharedEventSignInGuests],
  )

  // Load event: API first for published / signed-in access; merge with local seed; static last.
  useEffect(() => {
    let cancelled = false
    const loadEvent = async () => {
      if (!eventId) {
        if (!cancelled) {
          setEvent(null)
          setIsLoading(false)
        }
        return
      }

      let foundEvent: EventData | null =
        resolveStoredEventById(eventId) ?? null

      if (!foundEvent && isPublicDemoEventId(eventId)) {
        foundEvent = getPublicDemoEvent()
      }

      if (!isPublicDemoEventId(eventId)) {
        try {
          const apiRow = await fetchEventFromApi(eventId, {
            shareToken: urlShareToken,
          })
          if (!cancelled && apiRow) {
            const apiData = apiEventDtoToEventData(apiRow)
            foundEvent = apiData
            const uid = clerkUserId?.trim()
            const owner = apiData.createdByUserId?.trim()
            if (uid && owner && owner === uid) {
              registerServerCreatedEvent(apiData)
            }
          }
        } catch {
          /* offline — keep local/static below */
        }
      }

      if (!foundEvent && isPublicDemoEventId(eventId)) {
        foundEvent = getPublicDemoEvent()
      }

      if (!foundEvent) {
        const staticEvents: Record<string, EventData> = {
          "1": {
            id: "1",
            title: "Spring birthday dinner",
            date: "2026-03-04",
            time: "7:00 PM",
            location: "Harbor Way",
            address: "100 Harbor Way, Springfield",
            image:
              "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=800&h=600&fit=crop",
            description:
              "Join us for an evening of celebration, good food, and great company.",
            attendees: 0,
            isPublished: true,
            hostName: "Demo Host",
            hostAvatar:
              "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop",
            createdAt: new Date("2026-01-01T12:00:00Z"),
          },
          "draft_1": {
            id: "draft_1",
            title: "Kids birthday party",
            date: "2026-04-02",
            time: "3:00 PM",
            location: "Community center",
            address: "Downtown community center",
            image:
              "https://images.unsplash.com/photo-1558636508-e0db3814bd1d?w=800&h=600&fit=crop",
            description: "A fun afternoon celebration with games and cake.",
            attendees: 0,
            isPublished: false,
            hostName: "Demo Host",
            hostAvatar:
              "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop",
            createdAt: new Date("2026-01-01T12:00:00Z"),
          },
        }
        foundEvent = staticEvents[eventId] ?? null
      }

      if (!cancelled) {
        setEvent(foundEvent)
        setIsLoading(false)
      }
    }

    void loadEvent()
    return () => {
      cancelled = true
    }
  }, [eventId, clerkUserId, urlShareToken])

  const refreshEventFromApi = useCallback(async () => {
    try {
      const apiRow = await fetchEventFromApi(eventId, {
        shareToken: urlShareToken,
      })
      if (!apiRow) return
      const apiData = apiEventDtoToEventData(apiRow)
      setEvent((prev: EventData | null) => {
        if (!prev || prev.id !== apiRow.id) return prev
        const fresh = apiData
        const freshMedia = fresh.media ?? []
        const freshIds = new Set(freshMedia.map((m: EventMediaItem) => m.id))
        const prevMedia = prev.media ?? []
        const sticky = prevMedia.filter(
          (m: EventMediaItem) =>
            pendingUploadedAlbumMediaIdsRef.current.has(m.id) && !freshIds.has(m.id),
        )
        for (const m of freshMedia) {
          pendingUploadedAlbumMediaIdsRef.current.delete(m.id)
        }
        if (sticky.length === 0) {
          return fresh
        }
        const byId = new Map<string, EventMediaItem>()
        for (const m of freshMedia) {
          byId.set(m.id, m)
        }
        for (const m of sticky) {
          if (!byId.has(m.id)) byId.set(m.id, m)
        }
        const mergedMedia = [...byId.values()].sort(
          (a, b) =>
            eventMediaCreatedAtMs(b.createdAt) - eventMediaCreatedAtMs(a.createdAt),
        )
        return { ...fresh, media: mergedMedia }
      })
      router.refresh()
    } catch {
      /* offline */
    }
  }, [eventId, urlShareToken, router])

  const {
    requestDelete,
    undo,
    snackbarOpen,
    blockedDeleteMediaId,
  } = useDeferredMediaDelete({
    commitDelete: async (mid) => {
      await deleteMedia(mid)
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(MEMORIES_REFRESH_EVENT))
      }
      await refreshEventFromApi()
    },
    onCommitError: (msg) =>
      setMediaNotice({ tone: "error", text: msg }),
  })

  useEffect(() => {
    if (isLoading || !event) return
    const mediaParam = searchParams.get("media")?.trim() || ""
    const tabParam = searchParams.get("tab")?.trim().toLowerCase() || ""
    if (!mediaParam && tabParam !== "photos") return

    const key = `${eventId}|${searchParams.toString()}`
    if (albumDeepLinkHandledKeyRef.current === key) return

    if (mediaParam) {
      setActiveTab("Photos")
      const item = (event.media ?? []).find(
        (m: EventMediaItem) =>
          m.id === mediaParam && isAlbumGalleryPurpose(m.purpose),
      )
      if (item) {
        albumDeepLinkHandledKeyRef.current = key
        setMediaHighlightId(mediaParam)
        if (item.type === "image") {
          setActivityOpenImageMediaId(mediaParam)
        } else if (item.type === "video") {
          setDeepLinkOpenVideoMediaId(mediaParam)
        }
        stripAlbumDeepLinkQueryFromBrowserUrl(router)
        return
      }

      const mediaLen = (event.media ?? []).length
      if (mediaLen === 0) {
        if (!authLoaded) return
        if (!clerkUserId?.trim()) return
        if (!albumDeepLinkRefreshDoneRef.current) {
          if (!albumDeepLinkRefreshPendingRef.current) {
            albumDeepLinkRefreshPendingRef.current = true
            void refreshEventFromApi().finally(() => {
              albumDeepLinkRefreshPendingRef.current = false
              albumDeepLinkRefreshDoneRef.current = true
            })
          }
          return
        }
      }

      albumDeepLinkHandledKeyRef.current = key
      stripAlbumDeepLinkQueryFromBrowserUrl(router)
      return
    }

    if (tabParam === "photos") {
      albumDeepLinkHandledKeyRef.current = key
      setActiveTab("Photos")
      stripAlbumDeepLinkQueryFromBrowserUrl(router)
    }
  }, [
    authLoaded,
    clerkUserId,
    event,
    eventId,
    isLoading,
    refreshEventFromApi,
    router,
    searchParams,
  ])

  useEffect(() => {
    if (isLoading || !event) return
    if (searchParams.get("tab")?.trim().toLowerCase() !== "guests") return
    setActiveTab("Guests")
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (url.searchParams.get("tab")?.trim().toLowerCase() !== "guests") return
    url.searchParams.delete("tab")
    const qs = url.searchParams.toString()
    router.replace(`${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`, {
      scroll: false,
    })
  }, [event, eventId, isLoading, router, searchParams])

  const handleRsvpSettled = useCallback(async () => {
    await refreshEventFromApi()
    await refreshUserEvents()
  }, [refreshEventFromApi, refreshUserEvents])

  const {
    userStatus: rsvpWireStatus,
    setStatus: setRsvpStatus,
    isPending: isRsvpPending,
  } = useRsvp(eventId, {
    serverMine: event?.rsvpStatus,
    onInvalidate: handleRsvpSettled,
  })

  /** Align local state with RSVP API response before full event refetch completes. */
  const applyRsvpFromSnapshot = useCallback((snap: EventRsvpSnapshot) => {
    setEvent((prev: EventData | null) =>
      prev
        ? {
            ...prev,
            isRsvped: snap.mine != null,
            rsvpStatus: snap.mine ?? undefined,
          }
        : prev,
    )
  }, [])

  const pickRsvp = useCallback(
    async (status: PersistedRsvpStatus) => {
      if (!event || isPublicDemo) return
      if (rsvpWireStatus === status) return

      setRsvpNotice(null)
      const snap = await setRsvpStatus(status)
      if (!snap) {
        setRsvpNotice({
          tone: "error",
          text: "Couldn't save RSVP. Try again.",
        })
        return
      }
      applyRsvpFromSnapshot(snap)
      const labels: Record<PersistedRsvpStatus, string> = {
        going: "You're marked as going.",
        maybe: "Marked as maybe.",
        declined: "You're not going.",
      }
      setRsvpNotice({ tone: "success", text: labels[status] })
    },
    [
      event,
      isPublicDemo,
      rsvpWireStatus,
      setRsvpStatus,
      applyRsvpFromSnapshot,
    ],
  )

  const replayPendingTimelineSave = useCallback(async () => {
    if (isTimelineSaved(eventId) || event?.isSavedByUser) return true
    const ok = await saveToTimeline(eventId)
    if (ok) {
      clearTimelineSaveReminderDismissSession(eventId)
      setTimelineReminderDismissed(false)
      setEvent((prev: EventData | null) =>
        prev ? { ...prev, isSavedByUser: true } : prev,
      )
      void refreshTimelineSaves()
      void refreshUserEvents()
    }
    return ok
  }, [
    eventId,
    event?.isSavedByUser,
    isTimelineSaved,
    refreshTimelineSaves,
    saveToTimeline,
    refreshUserEvents,
  ])

  const replayPendingRsvpAfterAuth = useCallback(
    async (
      status: PersistedRsvpStatus,
    ): Promise<"ok" | "already" | "fail"> => {
      if (!event || isPublicDemo) return "fail"
      if (rsvpWireStatus === status) return "already"
      const snap = await setRsvpStatus(status)
      if (!snap) return "fail"
      applyRsvpFromSnapshot(snap)
      return "ok"
    },
    [event, isPublicDemo, rsvpWireStatus, setRsvpStatus, applyRsvpFromSnapshot],
  )

  const handleMediaUploaded = useCallback(
    (item?: EventMediaItem | null) => {
      if (item?.id) {
        pendingUploadedAlbumMediaIdsRef.current.add(item.id)
        setMediaHighlightId(item.id)
      }
      if (item) {
        setEvent((prev: EventData | null) => {
          if (!prev) return prev
          const nextMedia = [item, ...(prev.media ?? [])].sort(
            (a, b) =>
              eventMediaCreatedAtMs(b.createdAt) -
              eventMediaCreatedAtMs(a.createdAt),
          )
          return { ...prev, media: nextMedia }
        })
        setMediaNotice({
          tone: "success",
          text:
            item.type === "video"
              ? "Video added to the album."
              : "Photo added to the album.",
        })
      }
      void refreshEventFromApi()
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(MEMORIES_REFRESH_EVENT))
      }
    },
    [refreshEventFromApi],
  )

  const handleDeleteMedia = useCallback(
    (mediaId: string) => {
      if (!event || isPublicDemo) return
      const id = mediaId.trim()
      if (!id) return
      const prevMedia = event.media ?? []
      const idx = prevMedia.findIndex((m: EventMediaItem) => m.id === id)
      if (idx < 0) return
      const snapshot = prevMedia[idx]
      requestDelete({
        mediaId: id,
        applyOptimisticRemove: () => {
          setEvent((prev: EventData | null) => {
            if (!prev) return prev
            return {
              ...prev,
              media: (prev.media ?? []).filter((m: EventMediaItem) => m.id !== id),
            }
          })
        },
        restoreOptimistic: () => {
          setEvent((prev: EventData | null) => {
            if (!prev) return prev
            const m = [...(prev.media ?? [])]
            m.splice(idx, 0, snapshot)
            return { ...prev, media: m }
          })
        },
      })
    },
    [event, isPublicDemo, requestDelete],
  )

  useEffect(() => {
    if (!mediaNotice) return
    const ms = mediaNotice.entitlement ? 14000 : 4800
    const t = window.setTimeout(() => setMediaNotice(null), ms)
    return () => window.clearTimeout(t)
  }, [mediaNotice])

  useEffect(() => {
    if (!rsvpNotice) return
    const t = window.setTimeout(() => setRsvpNotice(null), 4800)
    return () => window.clearTimeout(t)
  }, [rsvpNotice])

  useEffect(() => {
    if (!publishShareBar) return
    const t = window.setTimeout(() => setPublishShareBar(false), 14000)
    return () => window.clearTimeout(t)
  }, [publishShareBar])

  useEffect(() => {
    if (!event) {
      setFallbackMapCoords(null)
      return
    }
    if (typeof event.lat === "number" && typeof event.lng === "number") {
      setFallbackMapCoords(null)
      return
    }
    const query = getDisplayAddress(event, "single").trim()
    if (!query) {
      setFallbackMapCoords(null)
      return
    }
    let cancelled = false
    void fetchGeocodeSuggestions(query)
      .then((rows) => {
        if (cancelled) return
        const first = rows[0]
        if (first && typeof first.lat === "number" && typeof first.lng === "number") {
          setFallbackMapCoords({ lat: first.lat, lng: first.lng })
        } else {
          setFallbackMapCoords(null)
        }
      })
      .catch(() => {
        if (!cancelled) setFallbackMapCoords(null)
      })
    return () => {
      cancelled = true
    }
  }, [event])

  useEffect(() => {
    if (!mediaHighlightId) return
    const t = window.setTimeout(() => setMediaHighlightId(null), 2400)
    return () => window.clearTimeout(t)
  }, [mediaHighlightId])

  const handleSaveToTimeline = useCallback(async () => {
    setTimelineSaveBusy(true)
    try {
      if (isTimelineSaved(eventId) || event?.isSavedByUser) {
        setMediaNotice({
          tone: "success",
          text: "Already saved.",
        })
        return
      }
      const ok = await saveToTimeline(eventId)
      if (!ok) {
        setMediaNotice({
          tone: "error",
          text: "Can't save this event right now.",
        })
        return
      }
      clearTimelineSaveReminderDismissSession(eventId)
      setTimelineReminderDismissed(false)
      setMediaNotice({ tone: "success", text: "Saved for later." })
      setEvent((prev: EventData | null) =>
        prev ? { ...prev, isSavedByUser: true } : prev,
      )
      void refreshTimelineSaves()
      void refreshUserEvents()
    } finally {
      setTimelineSaveBusy(false)
    }
  }, [
    eventId,
    event?.isSavedByUser,
    isTimelineSaved,
    refreshTimelineSaves,
    saveToTimeline,
    refreshUserEvents,
  ])

  const handleRemoveFromTimelineClick = useCallback(async () => {
    const ok = await removeFromTimeline(eventId)
    if (ok) {
      clearTimelineSaveReminderDismissSession(eventId)
      setTimelineReminderDismissed(false)
      setMediaNotice({
        tone: "success",
        text: "Removed from saved.",
      })
      setEvent((prev: EventData | null) =>
        prev ? { ...prev, isSavedByUser: false } : prev,
      )
      void refreshTimelineSaves()
      void refreshUserEvents()
    } else {
      setMediaNotice({
        tone: "error",
        text: "Couldn't remove from saved. Try again.",
      })
    }
  }, [eventId, removeFromTimeline, refreshTimelineSaves, refreshUserEvents])

  const processingVideoFingerprint = useMemo(() => {
    if (!event || isPublicDemo) return ""
    const rows = (event.media ?? []) as EventMediaItem[]
    const albumVideos = rows.filter(
      (m) =>
        m.type === "video" &&
        isAlbumGalleryPurpose(m.purpose) &&
        m.streamUid?.trim(),
    )
    const ids = albumVideos
      .filter(
        (m) =>
          m.status !== "error" &&
          (m.status === "processing" ||
            m.status === "uploading" ||
            !m.status),
      )
      .map((m) => m.id)
      .sort()
      .join(",")
    const processingTag =
      event.videoStatus === "processing" || event.videoStatus === "uploading"
        ? "processing"
        : ""
    return `${ids}|${processingTag}`
  }, [event, isPublicDemo])

  useEffect(() => {
    if (!processingVideoFingerprint || isPublicDemo) return
    if (!authLoaded || !clerkUserId?.trim()) return
    let cancelled = false
    let attempt = 0
    let timeoutId: number | undefined

    const step = () => {
      if (cancelled) return
      void (async () => {
        const sync = await postEventStreamMediaSyncFromApi(eventId, {
          shareToken: urlShareToken,
        })
        if (cancelled) return
        if (sync && sync.transitioned > 0) {
          await refreshEventFromApi()
        }
      })()
      attempt += 1
      const delayMs = Math.min(60_000, Math.round(4500 * Math.pow(1.35, attempt)))
      timeoutId = window.setTimeout(step, delayMs)
    }

    timeoutId = window.setTimeout(step, 4000)
    return () => {
      cancelled = true
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
  }, [
    authLoaded,
    clerkUserId,
    processingVideoFingerprint,
    eventId,
    isPublicDemo,
    refreshEventFromApi,
    urlShareToken,
  ])

  const isEventMgmtHost =
    event?.eventManagementRole === "owner" ||
    event?.eventManagementRole === "admin"

  const isUserHosting =
    !isPublicDemo &&
    (isEventMgmtHost ||
      event?.attendanceRole === "owner" ||
      Boolean(event?.isWorkspaceEventHost) ||
      (Boolean(clerkUserId) && event?.createdByUserId === clerkUserId) ||
      (event != null && isHosting(event.id)))

  const presentAsGuestViewer =
    !isUserHosting || (guestPreviewRequested && isUserHosting)
  const showHostManagementChrome = isUserHosting && !presentAsGuestViewer

  useEffect(() => {
    if (activeTab !== "Settings") return
    if (isPublicDemo || !showHostManagementChrome) {
      setActiveTab("Details")
    }
  }, [isPublicDemo, showHostManagementChrome, activeTab])

  useEffect(() => {
    return () => {
      if (copyLinkToastDismissRef.current) {
        clearTimeout(copyLinkToastDismissRef.current)
      }
      if (eventPageInlineCopyDismissRef.current) {
        clearTimeout(eventPageInlineCopyDismissRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isLoading || isPublicDemo || !event) {
      if (themeBeforeGuestViewRef.current !== undefined) {
        setTheme(themeBeforeGuestViewRef.current)
        themeBeforeGuestViewRef.current = undefined
      }
      return
    }
    const isGuestPublicViewer = presentAsGuestViewer
    if (!isGuestPublicViewer) {
      if (themeBeforeGuestViewRef.current !== undefined) {
        setTheme(themeBeforeGuestViewRef.current)
        themeBeforeGuestViewRef.current = undefined
      }
      return
    }
    if (themeBeforeGuestViewRef.current === undefined) {
      themeBeforeGuestViewRef.current = resolvedTheme ?? theme
    }
    const guestTheme = sharedPublicEventThemePreference(event.publicAppearanceDark)
    const currentTheme = resolvedTheme ?? theme
    if (currentTheme !== guestTheme) {
      setTheme(guestTheme)
    }
  }, [
    event,
    isLoading,
    isPublicDemo,
    presentAsGuestViewer,
    resolvedTheme,
    setTheme,
    theme,
  ])

  useEffect(() => {
    return () => {
      const saved = themeBeforeGuestViewRef.current
      if (saved !== undefined) {
        setTheme(saved)
        themeBeforeGuestViewRef.current = undefined
      }
    }
  }, [setTheme])

  useEffect(() => {
    if (isLoading || !event || isPublicDemo) {
      setTimelineReminderSoftEnter(false)
      return
    }
    if (timelineReminderDismissed) {
      setTimelineReminderSoftEnter(false)
      return
    }
    if (
      !event.isPublished ||
      !presentAsGuestViewer ||
      !authLoaded ||
      !clerkUserId?.trim() ||
      !timelineLoaded
    ) {
      setTimelineReminderSoftEnter(false)
      return
    }
    if (isTimelineSaved(event.id) || event.isSavedByUser) {
      setTimelineReminderSoftEnter(false)
      return
    }
    if (!shouldTimelineSaveReminderPulseSession(eventId)) {
      setTimelineReminderSoftEnter(false)
      return
    }
    setTimelineReminderSoftEnter(true)
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      markTimelineSaveReminderPulseConsumedSession(eventId)
    }
  }, [
    authLoaded,
    clerkUserId,
    event,
    eventId,
    isLoading,
    isPublicDemo,
    isTimelineSaved,
    presentAsGuestViewer,
    timelineLoaded,
    timelineReminderDismissed,
  ])

  usePendingEventReplay({
    router,
    eventId,
    event,
    isLoading,
    authLoaded,
    userId: clerkUserId,
    canReplayGuestActions: Boolean(
      authLoaded &&
        clerkUserId?.trim() &&
        event?.isPublished &&
        presentAsGuestViewer &&
        !isPublicDemo &&
        !isUserHosting,
    ),
    isUserHosting,
    timelineLoaded,
    saveTimelineQueryIntent: saveTimelineIntent,
    replayTimelineSave: replayPendingTimelineSave,
    isTimelineSaved,
    replayRsvp: replayPendingRsvpAfterAuth,
    onTimelineSuccess: (msg) => {
      clearTimelineSaveReminderDismissSession(eventId)
      setTimelineReminderDismissed(false)
      setMediaNotice({ tone: "success", text: msg })
    },
    onRsvpSuccess: (msg) => {
      setRsvpNotice({ tone: "success", text: msg })
    },
    onTimelineError: (msg) => {
      setMediaNotice({ tone: "error", text: msg })
    },
    onRsvpError: (msg) => {
      setRsvpNotice({ tone: "error", text: msg })
    },
    stripSaveTimelineQueryFromUrl: () =>
      stripSaveTimelineQueryFromBrowserUrl(router),
  })

  useEffect(() => {
    if (isLoading || !event || isPublicDemo) return
    const intent = resumeUploadIntent.toLowerCase()
    if (intent !== "1" && intent !== "true") return
    if (showHostManagementChrome) {
      stripResumeUploadQueryFromBrowserUrl(router)
      return
    }
    if (!authLoaded || !clerkUserId?.trim()) return

    const canUploadHere = resolveCanContributeEventMedia({
      isPublicDemo,
      isSignedIn: true,
      apiCanUploadMedia: event.canUploadMedia,
      apiCanUploadPhotos: event.canUploadPhotos,
      apiCanUploadVideos: event.canUploadVideos,
      isLocalHosting: showHostManagementChrome,
    })

    let existing = postAuthResumeUploadPromises.get(eventId)
    if (!existing) {
      existing = (async () => {
        try {
          setActiveTab("Photos")
          if (!canUploadHere) {
            stripResumeUploadQueryFromBrowserUrl(router)
            return
          }
          setMediaModalOpen(true)
          stripResumeUploadQueryFromBrowserUrl(router)
        } finally {
          postAuthResumeUploadPromises.delete(eventId)
        }
      })()
      postAuthResumeUploadPromises.set(eventId, existing)
    }
    void existing
  }, [
    authLoaded,
    clerkUserId,
    event,
    eventId,
    isLoading,
    isPublicDemo,
    showHostManagementChrome,
    resumeUploadIntent,
    router,
  ])

  const showSharedUploadCta = Boolean(
    event &&
      shouldSharedEventShowUploadCta({
        isPublicDemo,
        isUserHosting: showHostManagementChrome,
        isPublished: event.isPublished,
        allowGuestPhotoUploads: event.allowGuestPhotoUploads,
        allowGuestVideoUploads: event.allowGuestVideoUploads,
      }),
  )

  const openInMapsHref = useMemo(() => {
    if (!event) return null
    const lat = typeof event?.lat === "number" ? event.lat : null
    const lng = typeof event?.lng === "number" ? event.lng : null
    if (lat != null && lng != null) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`
    }
    const locationLine = getDisplayAddress(event, "single")
    if (!locationLine) return null
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationLine)}`
  }, [event])

  const storyScopeSource = useMemo(
    (): StoryScopeSource => ({
      seriesId: event?.seriesId ?? null,
      createdByUserId: event?.createdByUserId ?? null,
    }),
    [event?.seriesId, event?.createdByUserId],
  )

  const storyOutlineLabel =
    event?.seriesId?.trim() ? "Program story" : "Hosted story"

  const resolveEventShareUrl = useCallback(() => {
    return resolveClientEventShareAbsoluteUrl({
      eventId,
      event,
      isUserHosting,
      urlShareToken,
    })
  }, [event, eventId, isUserHosting, urlShareToken])

  const handleShare = useCallback(async () => {
    if (!event) return
    const url = resolveEventShareUrl()
    const sharePayload = buildEventWebSharePayload({
      event,
      absoluteUrl: url,
    })
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: sharePayload.title,
          text: sharePayload.text,
          url: sharePayload.url,
        })
        return
      } catch (e: unknown) {
        const name =
          e && typeof e === "object" && "name" in e
            ? String((e as { name: string }).name)
            : ""
        if (name === "AbortError") return
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    } catch {
      /* clipboard denied or unavailable */
    }
  }, [event, resolveEventShareUrl])

  if (isLoading) {
    return (
      <main
        className="app-page flex min-h-[40vh] min-w-0 items-center justify-center overflow-x-clip py-20"
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading event"
      >
        <div className="size-8 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground" />
      </main>
    )
  }

  if (!event) {
    return (
      <main className="app-page min-w-0 overflow-x-clip">
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 py-16">
          <div className="text-center">
            <h1 className="type-empty-title mb-2">Event not found</h1>
            <p className="type-body-sm mb-4 text-center">This event doesn&apos;t exist or has been removed.</p>
            <Link href={homeReady ? homeHref : "/"}>
              <Button className="rounded-full">{homeReady ? homeLabel : "Home"}</Button>
            </Link>
          </div>
        </div>
      </main>
    )
  }

  const coverLocationHeroLine = formatCoverLocationHeroSingleLine(event)

  const showGuestDetailsControls =
    !isPublicDemo &&
    authLoaded &&
    Boolean(clerkUserId) &&
    event.isPublished &&
    presentAsGuestViewer

  const showHostingAttendanceNote =
    !isPublicDemo &&
    authLoaded &&
    Boolean(clerkUserId) &&
    event.isPublished &&
    showHostManagementChrome

  const visibleTabs = isPublicDemo
    ? (["Details", "Photos", "Guests"] as const)
    : showHostManagementChrome
      ? tabs
      : (["Details", "Photos", "Guests"] as const)

  const isSignedIn = Boolean(clerkUserId)
  const isSignedOut = !isSignedIn
  /** Server flag + host fallback for stale client-only payloads missing `canViewEventMedia`. */
  const canRenderAlbumVideoOnDetails =
    event.canViewEventMedia === true ||
    (showHostManagementChrome && event.canViewEventMedia !== false)
  const viewAlbumSignInHref =
    !isPublicDemo &&
    event.requireSignInToViewEventAlbum === true &&
    !isSignedIn
      ? contributorPhotoSignInHref
      : null
  const canContributeMedia =
    authLoaded &&
    resolveCanContributeEventMedia({
      isPublicDemo,
      isSignedIn,
      apiCanUploadMedia: event.canUploadMedia,
      apiCanUploadPhotos: event.canUploadPhotos,
      apiCanUploadVideos: event.canUploadVideos,
      isLocalHosting: showHostManagementChrome,
    })

  /** Guests (including the public demo) can add published events to their timeline after sign-in. */
  const showPublicGuestTimelineCta = event.isPublished && presentAsGuestViewer
  const savedToTimeline = timelineLoaded
    ? isTimelineSaved(event.id) || event.isSavedByUser === true
    : event.isSavedByUser === true

  const showCalmTimelineSaveReminder =
    !isPublicDemo &&
    showPublicGuestTimelineCta &&
    authLoaded &&
    Boolean(clerkUserId) &&
    timelineLoaded &&
    !savedToTimeline &&
    !timelineReminderDismissed

  const resolvedAttendeeDisplayCount =
    event.attendeeCount ?? event.participantRoster?.length ?? 0

  const sharedGuestAccessHint =
    !isPublicDemo && event.isPublished && presentAsGuestViewer
      ? event.visibility === EVENT_VISIBILITY_LINK_ONLY
        ? "Private · Link access"
        : event.visibility === EVENT_VISIBILITY_INVITE_ONLY
          ? "Private · Invite only"
          : event.visibility === EVENT_VISIBILITY_PRIVATE
            ? "Private"
            : null
      : null

  /** Host / manager: copy on cover (below `sm`); `sm+` uses the secondary action row. */
  const showEventHeroCopyLinkOverlay =
    (event.isPublished && !presentAsGuestViewer) ||
    (!event.isPublished && showHostManagementChrome)

  const eventHeroOutlineActionClass =
    "h-10 min-w-fit shrink-0 gap-1.5 px-3 text-sm rounded-full border-border/50 bg-card/40 backdrop-blur-sm"

  /** Guest timeline row: Remove only when saved + signed-in + timeline ready. */
  const guestTimelineSecondaryRemove =
    savedToTimeline &&
    authLoaded &&
    Boolean(clerkUserId) &&
    timelineLoaded

  /** Secondary pills: full-width grid cells; no flex shrink/truncate on labels. */
  const guestSecondaryPillClass =
    "inline-flex h-10 w-full items-center justify-center gap-1 whitespace-nowrap rounded-full border border-border/50 bg-card/40 px-2 text-xs font-medium text-foreground backdrop-blur-sm transition hover:bg-muted/30 active:scale-[0.98]"

  const hostEventTimePhase = getEventTimeStatusPhase(event)
  const hostVisibilityLabel = formatEventVisibilityLabel(event.visibility)

  const backFallbackHref = navFrom ? eventNavFallbackHref(navFrom) : homeHref
  const backLabel = navFrom ? eventNavBackLabel(navFrom) : homeLabel
  const openInMapsAriaLabel = "Open location in maps"
  const mapLat =
    typeof event.lat === "number" ? event.lat : (fallbackMapCoords?.lat ?? null)
  const mapLng =
    typeof event.lng === "number" ? event.lng : (fallbackMapCoords?.lng ?? null)
  const safeEventLinks: NormalizedEventLink[] = (Array.isArray(event.links)
    ? event.links
    : []
  )
    .map((link: unknown) => parseEventLink(link))
    .filter((item: NormalizedEventLink | null): item is NormalizedEventLink =>
      Boolean(item),
    )
  const hasDescription =
    typeof event.description === "string" &&
    event.description.trim().length > 0
  const safeSupportMethods: NormalizedSupportMethod[] = (
    Array.isArray(event.contributions) ? event.contributions : []
  )
    .map((item: unknown) => parseSupportMethod(item))
    .filter((item: NormalizedSupportMethod | null): item is NormalizedSupportMethod =>
      Boolean(item),
    )

  /** Settings tab “Copy Event Link”: clipboard + bottom toast + row checkmark. */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(resolveEventShareUrl())
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
      if (copyLinkToastDismissRef.current) {
        clearTimeout(copyLinkToastDismissRef.current)
      }
      setCopyLinkToastOpen(true)
      copyLinkToastDismissRef.current = setTimeout(() => {
        setCopyLinkToastOpen(false)
        copyLinkToastDismissRef.current = null
      }, 2200)
    } catch {
      /* clipboard denied or unavailable */
    }
  }

  /** Mobile hero + desktop action row: inline ack only (no bottom toast). */
  const handleEventPageInlineCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(resolveEventShareUrl())
      setEventPageInlineCopyAck(true)
      if (eventPageInlineCopyDismissRef.current) {
        clearTimeout(eventPageInlineCopyDismissRef.current)
      }
      eventPageInlineCopyDismissRef.current = setTimeout(() => {
        setEventPageInlineCopyAck(false)
        eventPageInlineCopyDismissRef.current = null
      }, 1500)
    } catch {
      /* clipboard denied or unavailable */
    }
  }

  const copyEventLinkUrl = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedEventLinkId(id)
      setTimeout(() => setCopiedEventLinkId((prev) => (prev === id ? null : prev)), 1800)
    } catch {
      /* clipboard denied or unavailable */
    }
  }

  const copySupportValue = async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedSupportId(id)
      setTimeout(() => setCopiedSupportId((prev) => (prev === id ? null : prev)), 1800)
    } catch {
      /* clipboard denied or unavailable */
    }
  }

  const handleViewPublicPage = () => {
    window.open(
      appendGuestPreviewQuery(resolveEventShareUrl()),
      "_blank",
      "noopener,noreferrer",
    )
  }

  const handleCoverFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || !clerkUserId || isPublicDemo || !event || !showHostManagementChrome)
      return

    setCoverUploadError(null)
    const validationError = validateCoverUploadFile(file)
    if (validationError) {
      setCoverUploadError(validationError)
      return
    }

    setCoverUploading(true)
    try {
      const formData = uploadEventCoverImageFormData(event.id, file)

      const res = await fetch("/api/upload/image", {
        method: "POST",
        body: formData,
      })
      const json: unknown = await res.json()
      const envelope = json as {
        data: {
          id?: string
          cfImageId?: string
          viewerUrl?: string
          thumbnailUrl?: string
        } | null
        error: { message?: string } | null
      }

      if (!res.ok || envelope.error) {
        setCoverUploadError(mapUploadApiErrorToUserMessage(envelope.error))
        return
      }

      const data = envelope.data
      const mid = data?.id?.trim()
      const cfId = data?.cfImageId?.trim()
      if (!mid || !cfId) {
        setCoverUploadError(
          mapUploadApiErrorToUserMessage({
            code: "UPLOAD_FAILED_GENERIC",
          }),
        )
        return
      }

      const viewerUrl = data?.viewerUrl?.trim() ?? ""
      const thumbnailUrl = data?.thumbnailUrl?.trim() ?? viewerUrl
      const bust = Date.now()
      const heroBuilt = cfId
        ? buildCloudflareImageUrl(cfId, CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT).trim()
        : ""
      const thumbBuilt = cfId
        ? buildCloudflareImageUrl(cfId, CLOUDFLARE_EVENT_COVER_THUMB_VARIANT).trim()
        : ""

      setEvent((prev: EventData | null) => {
        if (!prev) return prev
        const image = heroBuilt
          ? appendCacheBustToImageUrl(heroBuilt, bust)
          : viewerUrl || prev.image
        const imageThumbnailUrl = thumbBuilt
          ? appendCacheBustToImageUrl(thumbBuilt, bust)
          : thumbnailUrl || prev.imageThumbnailUrl
        return {
          ...prev,
          image,
          imageThumbnailUrl,
        }
      })
      void refreshEventFromApi()
    } catch {
      setCoverUploadError("Network error. Check your connection and try again.")
    } finally {
      setCoverUploading(false)
    }
  }

  const serverOwnedByCurrentUser =
    Boolean(clerkUserId?.trim()) &&
    ((Boolean(event.createdByUserId?.trim()) &&
      event.createdByUserId === clerkUserId) ||
      Boolean(event.isWorkspaceEventHost))

  const handlePublish = async () => {
    if (isPublicDemo) return
    setIsPublishing(true)
    try {
      if (serverOwnedByCurrentUser) {
        const dto = await patchEventViaApi(event.id, { isPublished: true })
        const updated = apiEventDtoToEventData(dto)
        registerServerCreatedEvent(updated)
        setEvent(updated)
        await refreshUserEvents()
        await refreshEventFromApi()
        router.refresh()
      } else {
        publishEvent(event.id)
        setEvent({ ...event, isPublished: true })
      }
      setPublishShareBar(true)
    } catch (e: unknown) {
      if (e instanceof PlanEntitlementBlockedError) {
        setMediaNotice({ tone: "error", entitlement: e.detail })
      } else {
        setMediaNotice({
          tone: "error",
          text: "Couldn’t publish. Check your connection and try again.",
        })
      }
    } finally {
      setIsPublishing(false)
    }
  }

  const handleUnpublish = async () => {
    if (isPublicDemo) return
    try {
      if (serverOwnedByCurrentUser) {
        const dto = await patchEventViaApi(event.id, { isPublished: false })
        const updated = apiEventDtoToEventData(dto)
        registerServerCreatedEvent(updated)
        setEvent(updated)
        await refreshUserEvents()
        await refreshEventFromApi()
        router.refresh()
      } else {
        unpublishEvent(event.id)
        setEvent({ ...event, isPublished: false })
      }
      setShowUnpublishConfirm(false)
      setPublishShareBar(false)
    } catch {
      setMediaNotice({
        tone: "error",
        text: "Couldn’t remove from Story. Try again.",
      })
    }
  }

  const isHostArchived = Boolean(event?.hostArchivedAt?.trim())
  const canDeleteSeries = Boolean(
    event?.seriesId?.trim() &&
      clerkUserId?.trim() &&
      event.createdByUserId?.trim() === clerkUserId?.trim(),
  )


  const handleHostArchiveToggle = async (archived: boolean) => {
    if (isPublicDemo || !event) return
    setArchiveBusy(true)
    try {
      if (serverOwnedByCurrentUser) {
        const dto = await patchEventViaApi(event.id, { hostArchived: archived })
        const updated = apiEventDtoToEventData(dto)
        registerServerCreatedEvent(updated)
        setEvent(updated)
        await refreshUserEvents()
        await refreshEventFromApi()
        router.refresh()
      }
      setShowArchiveConfirm(false)
    } catch {
      setMediaNotice({
        tone: "error",
        text: archived
          ? "Couldn’t archive this event. Try again."
          : "Couldn’t restore this event. Try again.",
      })
    } finally {
      setArchiveBusy(false)
    }
  }

  const handleDelete = async () => {
    if (isPublicDemo || !event) return
    setDeleteBusy(true)
    try {
      const tryServer =
        Boolean(clerkUserId?.trim()) &&
        (HOSTING_EVENT_IDS.includes(event.id) ||
          Boolean(event.createdByUserId?.trim()))
      let removedInCatch = false
      if (tryServer) {
        try {
          await deleteEventViaApi(event.id, { scope: deleteScope })
        } catch (e) {
          if (e instanceof EventDeleteNotOnServerError) {
            deleteEvent(event.id)
            removedInCatch = true
          } else {
            setMediaNotice({
              tone: "error",
              text:
                e instanceof Error
                  ? e.message
                  : "Couldn’t delete this event. Try again.",
            })
            return
          }
        }
      }
      if (!removedInCatch) {
        deleteEvent(event.id)
      }
      setShowDeleteConfirm(false)
      setDeleteScope("default")
      void refreshUserEvents()
      router.refresh()
      router.push("/events")
    } finally {
      setDeleteBusy(false)
    }
  }

  const deleteConfirmTitle =
    deleteScope === "series" ? "Delete series permanently?" : "Delete permanently?"

  return (
    <>
    <MediaDeletedUndoSnackbar open={snackbarOpen} onUndo={undo} />
    {copyLinkToastOpen ? (
      <div
        role="status"
        aria-live="polite"
        className="surface-overlay z-toast pointer-events-none fixed bottom-8 left-1/2 max-w-[min(90vw,20rem)] -translate-x-1/2 px-3 py-1.5 text-center type-caption text-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
      >
        Link copied
      </div>
    ) : null}
    <main className="app-page min-w-0 overflow-x-clip">
      <EventDetailShell>
        {/* Back Button (authenticated app context only) */}
        {authLoaded && clerkUserId && !presentAsGuestViewer ? (
          <Link
            href={backFallbackHref}
            onClick={(e) => {
              if (typeof window !== "undefined" && window.history.length > 1) {
                e.preventDefault()
                router.back()
              }
            }}
            className="app-page-backlink-margin type-backlink-caps text-foreground/90 hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>{backLabel}</span>
          </Link>
        ) : null}

        {guestPreviewRequested && isUserHosting ? (
          <p
            className="type-caption mb-4 rounded-xl border border-border/50 bg-muted/25 px-3 py-2.5 text-muted-foreground"
            role="note"
          >
            Guest preview — same layout and visibility as members and link visitors.
            Your editing tab is unchanged.
          </p>
        ) : null}

        {/* Hero Section — unified text scrim + metadata; secondary actions sit below */}
        <EventCoverHeroStage
          src={eventCoverSrcHero(event)}
          alt={event.title}
          priority
          sizes={EVENT_COVER_DETAIL_SHELL_SIZES}
          footer={
            <div
              className={cn(
                "absolute bottom-0 left-0 right-0 z-[var(--z-cover-text)] max-w-full min-w-0 text-[var(--cover-hero-text-primary)]",
                "pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pl-4 pr-4 pt-5",
                "sm:pb-5 sm:pl-6 sm:pr-6 sm:pt-7 lg:px-8 lg:pb-6 lg:pt-9",
              )}
            >
              <div className="relative min-w-0 max-w-[min(100%,var(--max-width-readable))]">
                <div
                  className="overlay-text-scrim-inline -inset-x-2 -bottom-0.5 -top-0.5 sm:-inset-x-3"
                  aria-hidden
                />
                <div className="relative">
                  {event.isPublished ? (
                    <p
                      role="status"
                      className={cn(
                        "mb-2 inline-flex items-center gap-1.5 font-[family-name:var(--font-body)] text-xs font-medium uppercase leading-none tracking-[0.08em] sm:text-[13px]",
                        "text-[rgba(255,255,255,0.7)] [text-shadow:var(--cover-hero-text-shadow)]",
                      )}
                    >
                      <span
                        className="inline-block size-1 shrink-0 rounded-full bg-current opacity-60"
                        aria-hidden
                      />
                      Published
                    </p>
                  ) : null}
                  <h1
                    className={cn(
                      // `type-event-title` applies `text-foreground`; cover tokens for photo overlay (light + dark app chrome).
                      "type-event-title max-w-full text-balance !text-[var(--cover-hero-text-primary)]",
                      "leading-[1.18] sm:leading-snug",
                      "line-clamp-3 break-words [overflow-wrap:anywhere]",
                      "[text-shadow:var(--cover-hero-text-shadow)]",
                    )}
                  >
                    {event.title}
                  </h1>
                  {/* No `type-hero-meta` here: that utility applies `text-text-inverse`, which tracks app theme (often dark ink in `.dark`) and overrides cover-safe colors. */}
                  <div
                    className={cn(
                      "mt-2.5 flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1.5",
                      "font-[family-name:var(--font-body)] text-[length:var(--type-body-sm-size)] leading-[var(--type-body-sm-line)] tracking-[var(--type-tracking-body)] font-normal",
                      "text-[var(--cover-hero-text-secondary)]",
                      "[text-shadow:var(--cover-hero-text-shadow)]",
                      "[&_svg]:text-[var(--cover-hero-text-secondary)]",
                    )}
                    role="group"
                    aria-label="Event details"
                  >
                    <span className="inline-flex min-w-0 items-center gap-1.5 text-inherit">
                      <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="min-w-0 [overflow-wrap:anywhere] text-inherit">
                        {formatWeekdayLongMonthDayYear(event.date)}
                      </span>
                    </span>
                    {event.time ? (
                      <Fragment>
                        <span
                          className="select-none text-[color:color-mix(in_oklab,var(--cover-hero-text-secondary)_45%,transparent)]"
                          aria-hidden
                        >
                          ·
                        </span>
                        <span className="inline-flex min-w-0 items-center gap-1.5 text-inherit">
                          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          <span className="min-w-0 [overflow-wrap:anywhere] text-inherit">{event.time}</span>
                        </span>
                      </Fragment>
                    ) : null}
                    <span
                      className="select-none text-[color:color-mix(in_oklab,var(--cover-hero-text-secondary)_45%,transparent)]"
                      aria-hidden
                    >
                      ·
                    </span>
                    <span className="inline-flex min-w-0 max-w-full basis-full items-center gap-1.5 text-inherit sm:basis-auto sm:max-w-[min(100%,22rem)] lg:max-w-[26rem]">
                      <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span
                        className="min-w-0 flex-1 truncate text-left leading-normal"
                        aria-label={
                          getDisplayAddress(event, "single").trim() ||
                          coverLocationHeroLine.trim() ||
                          undefined
                        }
                      >
                        {coverLocationHeroLine}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }
        >
          <>
            {!isPublicDemo &&
            showHostManagementChrome &&
            !eventHasPersistedHeroCover(event) ? (
              <input
                ref={coverFileInputRef}
                type="file"
                accept={ALLOWED_EVENT_COVER_MIME_TYPES.join(",")}
                className="sr-only"
                aria-label="Upload event cover image"
                onChange={handleCoverFileChange}
              />
            ) : null}
            <div className="pointer-events-none absolute right-3 top-3 z-10 flex max-w-[calc(100%-1.5rem)] flex-row items-start justify-end gap-2">
              {!event.isPublished ? (
                <span
                  role="status"
                  title={
                    guestPreviewRequested
                      ? "Not visible to guests until published"
                      : undefined
                  }
                  className={cn(
                    "type-micro pointer-events-none inline-flex max-w-[min(100%,11rem)] shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-left type-weight-semibold uppercase tracking-[var(--type-hero-status-tracking)] shadow-sm backdrop-blur-sm sm:max-w-[13rem]",
                    "[text-shadow:0_1px_2px_rgb(0_0_0/0.55)]",
                    guestPreviewRequested
                      ? "border-[color:color-mix(in_oklab,white_22%,transparent)] bg-[color:color-mix(in_oklab,rgb(0_0_0/0.5)_72%,transparent)] text-white"
                      : "border-[color:color-mix(in_oklab,var(--warning)_42%,transparent)] bg-[color:color-mix(in_oklab,var(--warning)_18%,rgb(0_0_0/0.4))] text-amber-50",
                  )}
                >
                  {guestPreviewRequested ? (
                    <>
                      <EyeOff className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                      <span className="min-w-0 leading-tight">Hidden</span>
                    </>
                  ) : (
                    <>
                      <Edit className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                      <span>Draft</span>
                    </>
                  )}
                </span>
              ) : null}
              {(showEventHeroCopyLinkOverlay ||
                (!isPublicDemo &&
                  showHostManagementChrome &&
                  !eventHasPersistedHeroCover(event)) ||
                (presentAsGuestViewer && event.isPublished)) && (
                <div className="flex min-w-0 flex-col items-end gap-2">
                  {showEventHeroCopyLinkOverlay ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="pointer-events-auto flex h-9 w-9 min-h-9 min-w-9 shrink-0 items-center justify-center rounded-full border-0 bg-black/40 p-0 text-white shadow-none backdrop-blur-sm transition hover:bg-black/55 hover:text-white focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-0 sm:hidden"
                      onClick={() => void handleEventPageInlineCopyLink()}
                      aria-label={
                        eventPageInlineCopyAck
                          ? "Link copied"
                          : "Copy event link to clipboard"
                      }
                    >
                      {eventPageInlineCopyAck ? (
                        <CheckCircle
                          className="size-[18px] shrink-0 text-emerald-300 opacity-95 motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-200"
                          aria-hidden
                        />
                      ) : (
                        <Copy className="size-[18px] shrink-0 opacity-95" aria-hidden />
                      )}
                    </Button>
                  ) : null}
                  {!isPublicDemo &&
                  showHostManagementChrome &&
                  !eventHasPersistedHeroCover(event) ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="pointer-events-auto flex items-center rounded-full bg-background/90 shadow-md backdrop-blur-sm"
                        disabled={coverUploading}
                        onClick={() => coverFileInputRef.current?.click()}
                      >
                        {coverUploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          <Upload className="h-4 w-4" aria-hidden />
                        )}
                        <span className="ml-2">Upload cover</span>
                      </Button>
                      {coverUploadError ? (
                        <p
                          className="type-caption max-w-[12rem] rounded-md bg-destructive/90 px-2 py-1 text-destructive-foreground shadow"
                          role="alert"
                        >
                          {coverUploadError}
                        </p>
                      ) : null}
                    </>
                  ) : null}
                  {presentAsGuestViewer && event.isPublished ? (
                    <button
                      type="button"
                      className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full border-0 bg-black/40 p-0 text-white shadow-none backdrop-blur-sm transition hover:bg-black/55 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-0 sm:hidden"
                      onClick={() => void handleShare()}
                      aria-label={
                        shareCopied
                          ? "Link copied"
                          : "Share with another app or copy link"
                      }
                    >
                      {shareCopied ? (
                        <CheckCircle
                          className="h-5 w-5 shrink-0 text-emerald-300 opacity-95"
                          aria-hidden
                        />
                      ) : (
                        <Share2 className="h-5 w-5 shrink-0 opacity-70" aria-hidden />
                      )}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </>
        </EventCoverHeroStage>

        {showGuestDetailsControls && guestRsvpHeroSummary(event.rsvpStatus) ? (
          <p className="type-caption mt-2 text-muted-foreground/90">
            {guestRsvpHeroSummary(event.rsvpStatus)}
          </p>
        ) : null}
        {sharedGuestAccessHint ? (
          <p className="type-micro mt-1 text-muted-foreground">
            {sharedGuestAccessHint}
          </p>
        ) : null}

        {/* Secondary actions: below hero so title/metadata stay calm on all viewports */}
        <div
          className={cn(
            "mb-[var(--spacing-stack-section)] mt-4",
            event.isPublished && presentAsGuestViewer
              ? "w-full"
              : "flex min-w-0 flex-nowrap items-center justify-end gap-2 overflow-x-auto",
            isPublicDemo &&
              "rounded-2xl border border-border/40 bg-card/30 px-3 py-3 sm:px-4",
          )}
          role="region"
          aria-label="Event actions"
        >
          {event.isPublished ? (
            presentAsGuestViewer ? (
              <div className="flex w-full flex-col gap-1">
              {showPublicGuestTimelineCta ? (
                <div className="mt-3 flex flex-col gap-2">
                  {/* Primary: full-width only; never truncate */}
                  {!authLoaded ? (
                    <button
                      type="button"
                      disabled
                      className={cn(
                        "box-border flex h-11 w-full max-w-none items-center justify-center gap-2 rounded-full border border-border/45 bg-foreground/[0.06] text-sm font-medium text-foreground whitespace-nowrap transition",
                        "opacity-80",
                      )}
                      aria-busy
                      aria-label="Loading timeline"
                    >
                      <Loader2
                        className="h-4 w-4 shrink-0 animate-spin"
                        aria-hidden
                      />
                      Add to Timeline
                    </button>
                  ) : !clerkUserId ? (
                    <button
                      type="button"
                      className={cn(
                        "box-border flex h-11 w-full max-w-none items-center justify-center gap-2 rounded-full bg-black text-sm font-medium text-white whitespace-nowrap transition",
                        "active:scale-[0.99] dark:bg-white dark:text-black",
                      )}
                      aria-label="Sign in to add this event to your timeline"
                      onClick={() => beginAuthForGuestTimeline()}
                    >
                      <Plus className="h-4 w-4 shrink-0" aria-hidden />
                      Add to Timeline
                    </button>
                  ) : !timelineLoaded ? (
                    <button
                      type="button"
                      disabled
                      className={cn(
                        "box-border flex h-11 w-full max-w-none items-center justify-center gap-2 rounded-full border border-border/45 bg-foreground/[0.06] text-sm font-medium text-foreground whitespace-nowrap transition",
                        "opacity-80",
                      )}
                      aria-busy
                      aria-label="Loading timeline"
                    >
                      <Loader2
                        className="h-4 w-4 shrink-0 animate-spin"
                        aria-hidden
                      />
                      Add to Timeline
                    </button>
                  ) : savedToTimeline ? (
                    <button
                      type="button"
                      disabled
                      className={cn(
                        "box-border flex h-11 w-full max-w-none cursor-default items-center justify-center rounded-full border border-emerald-200 bg-emerald-500/10 text-sm font-medium whitespace-nowrap text-emerald-600 transition",
                        "dark:border-emerald-900/50 dark:text-emerald-400",
                      )}
                    >
                      Added ✓
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={cn(
                        "box-border flex h-11 w-full max-w-none items-center justify-center gap-2 rounded-full bg-black text-sm font-medium text-white whitespace-nowrap transition",
                        "active:scale-[0.99] dark:bg-white dark:text-black",
                        timelineSaveBusy && "pointer-events-none opacity-80",
                      )}
                      disabled={timelineSaveBusy}
                      aria-busy={timelineSaveBusy}
                      aria-label="Add this event to your timeline"
                      onClick={() => void handleSaveToTimeline()}
                    >
                      {timelineSaveBusy ? (
                        <Loader2
                          className="h-4 w-4 shrink-0 animate-spin"
                          aria-hidden
                        />
                      ) : (
                        <Plus className="h-4 w-4 shrink-0" aria-hidden />
                      )}
                      Add to Timeline
                    </button>
                  )}

                  {/* Secondary: grid (mobile: Share in hero; sm+: Share here) */}
                  <div
                    className={cn(
                      "grid w-full gap-2",
                      guestTimelineSecondaryRemove
                        ? "grid-cols-2 sm:grid-cols-3"
                        : "grid-cols-1 sm:grid-cols-2",
                    )}
                  >
                    {guestTimelineSecondaryRemove ? (
                      <button
                        type="button"
                        className={guestSecondaryPillClass}
                        aria-label="Remove from timeline"
                        onClick={() => void handleRemoveFromTimelineClick()}
                      >
                        <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Remove</span>
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={cn(
                        guestSecondaryPillClass,
                        "hidden sm:inline-flex",
                      )}
                      onClick={() => void handleShare()}
                      aria-label={
                        shareCopied
                          ? "Link copied"
                          : "Share with another app or copy link"
                      }
                    >
                      {shareCopied ? (
                        <CheckCircle
                          className="h-4 w-4 shrink-0 text-success"
                          aria-hidden
                        />
                      ) : (
                        <Share2
                          className="h-4 w-4 shrink-0 opacity-80"
                          aria-hidden
                        />
                      )}
                      <span>{shareCopied ? "Copied" : "Share"}</span>
                    </button>
                    <StoryScopeOutlineLink
                      source={storyScopeSource}
                      focusEventId={event.id}
                      label={
                        <>
                          <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
                          <span className="sr-only sm:hidden">Story</span>
                          <span className="hidden sm:inline">Story</span>
                        </>
                      }
                      className={cn(
                        guestSecondaryPillClass,
                        "!h-10 !min-h-0 px-2 text-xs font-medium",
                      )}
                    />
                  </div>
                </div>
              ) : (
                <div className="mt-3 grid w-full min-w-0 grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn(
                      eventHeroOutlineActionClass,
                      "w-full justify-center whitespace-nowrap",
                    )}
                    onClick={() => void handleShare()}
                    aria-label={
                      shareCopied
                        ? "Link copied"
                        : "Share with another app or copy link"
                    }
                  >
                    {shareCopied ? (
                      <CheckCircle className="mr-1.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                    ) : (
                      <Share2 className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                    )}
                    {shareCopied ? "Copied" : "Share"}
                  </Button>
                  <StoryScopeOutlineLink
                    source={storyScopeSource}
                    focusEventId={event.id}
                    label={storyOutlineLabel}
                    className={cn(
                      eventHeroOutlineActionClass,
                      "w-full justify-center whitespace-nowrap",
                    )}
                  />
                </div>
              )}
              {showPublicGuestTimelineCta && authLoaded && !clerkUserId ? (
                <p className="type-caption text-muted-foreground">
                  Sign in to save this event for later
                </p>
              ) : null}
              {showCalmTimelineSaveReminder ? (
                <div
                  role="status"
                  className={cn(
                    "mt-2 flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border/30 bg-secondary/[0.12] px-3 py-2.5 sm:px-3.5",
                    timelineReminderSoftEnter &&
                      "timeline-save-reminder--enter-once",
                  )}
                  onAnimationEnd={(e) => {
                    if (e.animationName !== "maatii-timeline-reminder-soft") {
                      return
                    }
                    markTimelineSaveReminderPulseConsumedSession(event.id)
                    setTimelineReminderSoftEnter(false)
                  }}
                >
                  <p className="type-body-sm min-w-0 flex-1 text-muted-foreground">
                    Save this event for later
                  </p>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="type-button h-8 rounded-full border-border/45 px-3"
                      disabled={timelineSaveBusy}
                      aria-busy={timelineSaveBusy}
                      onClick={() => void handleSaveToTimeline()}
                    >
                      {timelineSaveBusy ? (
                        <Loader2
                          className="h-3.5 w-3.5 animate-spin"
                          aria-hidden
                        />
                      ) : (
                        "Add to Timeline"
                      )}
                    </Button>
                    <button
                      type="button"
                      onClick={dismissCalmTimelineReminder}
                      className="rounded-full p-1.5 text-muted-foreground/65 transition-colors hover:bg-muted/35 hover:text-foreground"
                      aria-label="Dismiss reminder"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </div>
              ) : null}
              </div>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={eventHeroOutlineActionClass}
                  onClick={handleViewPublicPage}
                  aria-label="Open guest preview of this event in a new tab"
                >
                  <ExternalLink className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  View
                </Button>
                <StoryScopeOutlineLink
                  source={storyScopeSource}
                  focusEventId={event.id}
                  label={storyOutlineLabel}
                  className={eventHeroOutlineActionClass}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    eventHeroOutlineActionClass,
                    "hidden sm:inline-flex transition-[color,transform,box-shadow] duration-200 motion-reduce:transition-none",
                  )}
                  onClick={() => void handleEventPageInlineCopyLink()}
                  aria-label={
                    eventPageInlineCopyAck ? "Link copied" : "Copy event link"
                  }
                >
                  {eventPageInlineCopyAck ? (
                    <CheckCircle
                      className="mr-1.5 h-4 w-4 shrink-0 text-success motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-200"
                      aria-hidden
                    />
                  ) : (
                    <Copy className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  )}
                  {eventPageInlineCopyAck ? "Copied" : "Copy link"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={eventHeroOutlineActionClass}
                  onClick={() => void handleShare()}
                  aria-label={
                    shareCopied
                      ? "Link copied"
                      : "Share with another app or copy link"
                  }
                >
                  {shareCopied ? (
                    <CheckCircle className="mr-1.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                  ) : (
                    <Share2 className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  )}
                  {shareCopied ? "Copied" : "Share"}
                </Button>
              </>
            )
          ) : showHostManagementChrome ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={eventHeroOutlineActionClass}
                onClick={handleViewPublicPage}
                aria-label="Open guest preview of this event in a new tab"
              >
                <ExternalLink className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                View
              </Button>
              <StoryScopeOutlineLink
                source={storyScopeSource}
                focusEventId={event.id}
                label={storyOutlineLabel}
                className={eventHeroOutlineActionClass}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  eventHeroOutlineActionClass,
                  "hidden sm:inline-flex transition-[color,transform,box-shadow] duration-200 motion-reduce:transition-none",
                )}
                onClick={() => void handleEventPageInlineCopyLink()}
                aria-label={
                  eventPageInlineCopyAck ? "Link copied" : "Copy event link"
                }
              >
                {eventPageInlineCopyAck ? (
                  <CheckCircle
                    className="mr-1.5 h-4 w-4 shrink-0 text-success motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-200"
                    aria-hidden
                  />
                ) : (
                  <Copy className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                )}
                {eventPageInlineCopyAck ? "Copied" : "Copy link"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={eventHeroOutlineActionClass}
                onClick={() => void handleShare()}
                aria-label={shareCopied ? "Link copied" : "Share with another app or copy link"}
              >
                {shareCopied ? (
                  <CheckCircle className="mr-1.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                ) : (
                  <Share2 className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                )}
                {shareCopied ? "Copied" : "Share"}
              </Button>
            </>
          ) : (
            <>
              <StoryScopeOutlineLink
                source={storyScopeSource}
                focusEventId={event.id}
                label={storyOutlineLabel}
                className={eventHeroOutlineActionClass}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={eventHeroOutlineActionClass}
                onClick={() => void handleShare()}
                aria-label={shareCopied ? "Link copied" : "Share with another app or copy link"}
              >
                {shareCopied ? (
                  <CheckCircle className="mr-1.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                ) : (
                  <Share2 className="mr-1.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                )}
                {shareCopied ? "Copied" : "Share"}
              </Button>
            </>
          )}
          {eventPageInlineCopyAck ? (
            <span className="sr-only" role="status" aria-live="polite">
              Link copied to clipboard
            </span>
          ) : null}
        </div>

        {/* Tabs — strict grid on mobile so four labels stay on one row; flex capsule on sm+. */}
        <div
          className={cn(
            "surface-pill-toolbar mb-[var(--spacing-stack-section)] w-full max-w-full min-w-0 gap-[var(--system-pill-track-gap)] overflow-hidden rounded-full",
            "grid w-full sm:flex sm:w-fit sm:flex-nowrap sm:grid-cols-none",
            visibleTabs.length === 3 ? "grid-cols-3" : "grid-cols-4",
          )}
        >
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              aria-label={tab === "Settings" ? "Settings" : undefined}
              onClick={() => setActiveTab(tab)}
              className={cn(
                "focus-ring type-button system-segmented-item flex min-h-10 min-w-0 w-full items-center justify-center whitespace-nowrap rounded-full px-2 py-2 text-center transition-[color,background-color,font-weight,box-shadow] duration-200 sm:w-auto sm:px-5 sm:py-2.5",
                activeTab === tab
                  ? "system-segmented-item--active bg-foreground text-background"
                  : "system-segmented-item--idle",
              )}
            >
              {tab === "Settings" ? (
                <>
                  <span className="max-[380px]:hidden">Settings</span>
                  <span className="hidden max-[380px]:inline">Manage</span>
                </>
              ) : (
                tab
              )}
            </button>
          ))}
        </div>

        {mediaNotice?.entitlement ? (
          <div className="mb-6">
            <BillingEntitlementBlocked
              payload={mediaNotice.entitlement}
              compact
              analyticsSurface="event_detail"
            />
          </div>
        ) : mediaNotice?.text ? (
          <div
            role="status"
            className={cn(
              "type-body-sm mb-6 rounded-2xl border px-4 py-3 transition-opacity animate-in fade-in slide-in-from-top-1 duration-300",
              mediaNotice.tone === "success"
                ? "border-border/60 bg-card/80 text-foreground"
                : "border-destructive/25 bg-destructive/5 text-destructive",
            )}
          >
            {mediaNotice.text}
          </div>
        ) : null}

        {publishShareBar && showHostManagementChrome && event.isPublished ? (
          <div
            role="status"
            className="type-body-sm mb-6 flex flex-col gap-3 rounded-2xl border border-border/60 bg-card/80 px-4 py-3 animate-in fade-in slide-in-from-top-1 duration-300 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="type-body type-weight-medium">Event published</p>
            <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto">
              <Button
                type="button"
                variant="inverse"
                size="sm"
                className="h-10 min-w-fit shrink-0 px-3 text-sm rounded-full"
                onClick={handleViewPublicPage}
              >
                View
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-10 min-w-fit shrink-0 px-3 text-sm rounded-full text-muted-foreground"
                onClick={() => setPublishShareBar(false)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        ) : null}

        {/* Tab Content */}
        {activeTab === "Details" && (
          <div className="min-w-0 space-y-6">
              {event.seriesId?.trim() ? (
                <div className="surface-panel flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="type-body-sm text-muted-foreground">
                    Part of a recurring program—each week is its own chapter.
                  </p>
                  <RecurringGuideTrigger variant="inline" className="self-start sm:self-auto" />
                </div>
              ) : null}
              {hasDescription && (
                <div className="surface-panel">
                  <h2 className="type-card-title mb-3">About this event</h2>
                  <p className="type-body line-clamp-3 break-words text-muted-foreground [overflow-wrap:anywhere]">
                    {event.description}
                  </p>
                </div>
              )}

              {canRenderAlbumVideoOnDetails &&
                !(event.media ?? []).some((m: EventMediaItem) => m.type === "video") &&
                (event.videoEmbedUrl ||
                  event.videoStatus === "processing" ||
                  event.videoStatus === "uploading" ||
                  event.videoStatus === "error") && (
                <div className="space-y-2">
                  <h2 className="type-card-title">Event video</h2>
                  <EventVideoSection
                    eventTitle={event.title}
                    videoStatus={event.videoStatus}
                    videoHlsUrl={event.videoHlsUrl}
                    videoEmbedUrl={event.videoEmbedUrl}
                    videoPosterUrl={event.videoThumbnailUrl}
                  />
                </div>
              )}

              {/* Location */}
              <div className="surface-panel">
                <h2 className="type-card-title mb-3">Location</h2>
                <EventLocationCompactCard
                  event={event}
                  openInMapsHref={openInMapsHref}
                  mapLat={mapLat}
                  mapLng={mapLng}
                  ariaLabel={openInMapsAriaLabel}
                />
              </div>

              {/* Links Section */}
              {safeEventLinks.length > 0 && (
                <div className="surface-panel">
                  <h2 className="type-card-title mb-4">Links</h2>
                  <div className="space-y-2">
                    {safeEventLinks.map((link) => (
                      <div
                        key={link.id}
                        className="flex items-center gap-3 rounded-xl bg-secondary/50 p-3"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="type-body type-weight-medium truncate">{link.label}</p>
                          <p className="type-caption truncate">
                            {link.normalizedUrl}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="type-button-compact h-9 rounded-full px-3"
                            onClick={() =>
                              void copyEventLinkUrl(link.id, link.normalizedUrl)
                            }
                            aria-label={
                              copiedEventLinkId === link.id
                                ? `${link.label} copied`
                                : `Copy ${link.label} link`
                            }
                          >
                            {copiedEventLinkId === link.id ? "Copied" : "Copy"}
                          </Button>
                          <Button
                            asChild
                            type="button"
                            variant="outline"
                            size="sm"
                            className="type-button-compact h-9 rounded-full px-3"
                          >
                            <a
                              href={link.normalizedUrl}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              aria-label={`Open ${link.label}`}
                            >
                              Open
                            </a>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contributions Section */}
              {safeSupportMethods.length > 0 && (
                <div className="surface-panel">
                  <h2 className="type-card-title mb-4">Support the Event</h2>
                  <p className="type-body-sm mb-4">
                    Show your support through these payment options
                  </p>
                  <div className="space-y-2">
                    {safeSupportMethods.map((method) => {
                      const openUrl = getSupportMethodOpenUrl(method)
                      const copyValue = getSupportMethodCopyValue(method)
                      const displayValue = getSupportMethodDisplayValue(method)
                      return (
                        <div
                          key={method.id}
                          className="flex items-center gap-3 rounded-xl bg-secondary/50 p-3"
                        >
                          <span className="type-caption type-weight-semibold flex h-8 w-8 items-center justify-center rounded-lg bg-background uppercase">
                            {method.provider.slice(0, 2)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="type-body type-weight-medium capitalize">
                              {method.provider}
                            </p>
                            <p className="type-caption truncate">
                              {displayValue}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="type-button-compact h-9 rounded-full px-3"
                              onClick={() => void copySupportValue(method.id, copyValue)}
                              aria-label={
                                copiedSupportId === method.id
                                  ? `${method.provider} copied`
                                  : `Copy ${method.provider} value`
                              }
                            >
                              {copiedSupportId === method.id ? "Copied" : "Copy"}
                            </Button>
                            {openUrl ? (
                              <Button
                                asChild
                                type="button"
                                variant="outline"
                                size="sm"
                                className="type-button-compact h-9 rounded-full px-3"
                              >
                                <a
                                  href={openUrl}
                                  target="_blank"
                                  rel="noopener noreferrer nofollow"
                                  aria-label={`Open ${method.provider}`}
                                >
                                  Open
                                </a>
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Host */}
              <div className="surface-panel">
                <h2 className="type-section-label mb-4">Hosted by</h2>
                <div className="flex items-center gap-3">
                  <div className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-border">
                    <Image
                      src={event.hostAvatar || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=96&h=96&q=68"}
                      alt={event.hostName}
                      fill
                      sizes="48px"
                      quality={IMAGE_QUALITY_AVATAR}
                      className="object-cover"
                    />
                  </div>
                  <div>
                    <p className="type-body type-weight-medium">{event.hostName}</p>
                    <p className="type-body-sm">Organizer</p>
                  </div>
                </div>
              </div>

              {event.isPublished && presentAsGuestViewer && authLoaded ? (
                <div className="surface-panel-compact">
                  <div className="flex flex-col gap-3">
                    <div className="min-w-0">
                      <h3 className="type-panel-label">RSVP</h3>
                      <p className="type-caption">
                        Your attendance response for the host — separate from
                        saving the event for later
                      </p>
                    </div>
                    {!clerkUserId ? (
                      <EventRsvpChoiceButtons
                        value={undefined}
                        disabled={false}
                        onPick={(status) => {
                          beginAuthForGuestRsvp(status)
                        }}
                      />
                    ) : showGuestDetailsControls && !isPublicDemo ? (
                      <>
                        <EventRsvpChoiceButtons
                          value={
                            rsvpWireStatus === "no_response"
                              ? undefined
                              : rsvpWireStatus
                          }
                          disabled={isRsvpPending}
                          onPick={pickRsvp}
                        />
                        {rsvpNotice ? (
                          <p
                            role="status"
                            className={cn(
                              "type-caption",
                              rsvpNotice.tone === "success"
                                ? ""
                                : "text-destructive",
                            )}
                          >
                            {rsvpNotice.text}
                          </p>
                        ) : null}
                      </>
                    ) : clerkUserId && isPublicDemo ? (
                      <p className="type-caption">
                        On a live invite, choose Going, Maybe, or Not going — your host sees it here.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {event.isPublished && presentAsGuestViewer && !isSignedOut ? (
                <div className="surface-panel">
                  <h2 className="type-section-label mb-4">
                    Attendees ({resolvedAttendeeDisplayCount})
                  </h2>
                  {(event.participantRoster ?? []).length > 0 ? (
                    <div className="flex flex-wrap gap-3">
                      {(event.participantRoster ?? []).map(
                        (attendee: EventParticipantRosterItem) => (
                        <div
                          key={attendee.userId}
                          className="flex items-center gap-2 rounded-full bg-secondary/50 py-1 pl-1 pr-3"
                        >
                          <div className="relative h-7 w-7 shrink-0 overflow-hidden rounded-full">
                            {attendee.avatarUrl ? (
                              <Image
                                src={attendee.avatarUrl}
                                alt={attendee.displayName}
                                fill
                                sizes="28px"
                                quality={IMAGE_QUALITY_AVATAR}
                                className="object-cover"
                              />
                            ) : (
                              <div className="type-micro type-weight-medium flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                                {attendee.displayName
                                  .trim()
                                  .slice(0, 1)
                                  .toUpperCase() ||
                                  "·"}
                              </div>
                            )}
                          </div>
                          <span className="type-body-sm text-foreground type-weight-medium">
                            {attendee.displayName}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : isPublicDemo ? (
                    <>
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-2 rounded-full bg-secondary/50 py-1 pl-1 pr-3">
                          <div className="relative h-7 w-7 shrink-0 overflow-hidden rounded-full">
                            <Image
                              src={
                                event.hostAvatar?.trim() ||
                                "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=64&h=64&q=68"
                              }
                              alt="Example guest"
                              fill
                              sizes="28px"
                              quality={IMAGE_QUALITY_AVATAR}
                              className="object-cover"
                            />
                          </div>
                          <span className="type-body-sm text-foreground type-weight-medium">Amara O.</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-full bg-secondary/50 py-1 pl-1 pr-3">
                          <div className="type-micro type-weight-medium flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                            KE
                          </div>
                          <span className="type-body-sm text-foreground type-weight-medium">Kenna E.</span>
                        </div>
                      </div>
                      <p className="type-caption mt-4">
                        Sample list — on your events, real guests appear here as they respond.
                      </p>
                    </>
                  ) : (
                    <p className="type-body-sm">
                      Responses will appear here as guests RSVP.
                    </p>
                  )}
                </div>
              ) : null}

              {showHostManagementChrome ? (
                <div className="surface-panel">
                  <div className="grid min-w-0 grid-cols-2 gap-3 sm:gap-4">
                    <div className="text-center p-4 rounded-xl bg-secondary/50">
                      <Users className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                      <p className="type-stat-value mb-0">
                        {event.attendeeCount ?? event.attendees ?? 0}
                      </p>
                      <p className="type-caption">Attending</p>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-secondary/50">
                      {event.isPublished ? (
                        <>
                          {hostEventTimePhase === "upcoming" ? (
                            <Clock className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                          ) : hostEventTimePhase === "live" ? (
                            <Radio className="w-5 h-5 mx-auto mb-2 text-success" />
                          ) : (
                            <CheckCircle className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                          )}
                          <p
                            className={cn(
                              "type-stat-value mb-0",
                              hostEventTimePhase === "live" && "text-success",
                            )}
                          >
                            {hostEventTimePhase === "upcoming"
                              ? "Upcoming"
                              : hostEventTimePhase === "live"
                                ? "Live"
                                : "Past"}
                          </p>
                          <p className="type-caption">
                            {hostVisibilityLabel}
                          </p>
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                          <p className="type-stat-value mb-0">Draft</p>
                          <p className="type-caption">Private</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {showHostingAttendanceNote ? (
                <div className="surface-panel-compact">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="type-panel-label">Attendance</h3>
                      <p className="type-caption">
                        You&apos;re hosting this event
                      </p>
                    </div>
                    <Users
                      className="h-5 w-5 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                  </div>
                </div>
              ) : null}

              {/* Actions for host */}
              {showHostManagementChrome && (
                <div className="space-y-3">
                  {!event.isPublished ? (
                    <>
                      <Button
                        variant="inverse"
                        onClick={handlePublish}
                        disabled={isPublishing}
                        className="w-full rounded-full"
                      >
                        {isPublishing ? (
                          <>
                            <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin mr-2" />
                            Publishing...
                          </>
                        ) : (
                          <>
                            <Globe className="w-4 h-4 mr-2" />
                            Publish Event
                          </>
                        )}
                      </Button>
                      <Link href={`/edit/${event.id}`} className="block">
                        <Button variant="outline" className="w-full rounded-full">
                          <Edit className="w-4 h-4 mr-2" />
                          Continue Editing
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <Link href={`/edit/${event.id}`} className="block">
                      <Button variant="outline" className="w-full rounded-full">
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Event
                      </Button>
                    </Link>
                  )}
                </div>
              )}
          </div>
        )}

        {activeTab === "Photos" && (
          <div className="space-y-0">
            {isSignedOut ? (
              <div className="surface-panel">
                <h2 className="type-card-title">Photos</h2>
                <p className="type-body-sm mt-2">
                  Sign in to see photos, moments, event comments, or add your own.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="mt-4 rounded-full"
                  asChild
                >
                  <Link href={contributorPhotoSignInHref}>Sign in</Link>
                </Button>
              </div>
            ) : (
              <>
                <EventPhotosPanel
                  eventTitle={event.title}
                  eventDateLabelForViewer={formatEventDateTimeLine(
                    event.date,
                    event.time,
                  )}
                  media={event.media ?? []}
                  currentUserId={clerkUserId ?? null}
                  eventCreatedByUserId={event.createdByUserId}
                  guestAlbumViewer={presentAsGuestViewer}
                  canContribute={canContributeMedia}
                  isSignedIn={isSignedIn}
                  albumAccessHint={
                    showSharedUploadCta && !canContributeMedia
                      ? isSignedIn
                        ? { kind: "add_to_timeline" as const }
                        : { kind: "sign_in" as const, href: contributorPhotoSignInHref }
                      : undefined
                  }
                  viewAlbumSignInHref={viewAlbumSignInHref}
                  highlightMediaId={mediaHighlightId}
                  focusOpenImageMediaId={activityOpenImageMediaId}
                  onFocusOpenImageConsumed={() => setActivityOpenImageMediaId(null)}
                  focusOpenVideoMediaId={deepLinkOpenVideoMediaId}
                  onFocusOpenVideoConsumed={() => setDeepLinkOpenVideoMediaId(null)}
                  onAddClick={() => {
                    if (!clerkUserId?.trim()) {
                      router.push(contributorPhotoUploadSignInHref)
                      return
                    }
                    setMediaModalOpen(true)
                  }}
                  onDeleteMedia={
                    isPublicDemo || !clerkUserId ? undefined : handleDeleteMedia
                  }
                  deletingMediaId={blockedDeleteMediaId}
                />
                {!isPublicDemo ? (
                  <EventActivitySection
                    eventId={event.id}
                    shareToken={urlShareToken}
                    onOpenGuestsTab={() => setActiveTab("Guests")}
                    onOpenMedia={(mediaId) => {
                      setActiveTab("Photos")
                      setMediaHighlightId(mediaId)
                      setActivityOpenImageMediaId(mediaId)
                    }}
                  />
                ) : null}
              </>
            )}
          </div>
        )}

        {activeTab === "Guests" && (
          <div className="surface-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="type-card-title">Guest List</h2>
              {!isSignedOut ? (
                <span className="type-body-sm">
                  {resolvedAttendeeDisplayCount}{" "}
                  {resolvedAttendeeDisplayCount === 1 ? "guest" : "guests"}
                </span>
              ) : null}
            </div>
            {isSignedOut ? (
              <div className="rounded-xl border border-border/40 bg-secondary/15 px-4 py-6 text-center sm:py-8">
                <p className="type-body-sm type-weight-medium text-foreground/90">
                  Sign in to see the guest list.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="mt-4 rounded-full"
                  asChild
                >
                  <Link href={contributorGuestSignInHref}>Sign in</Link>
                </Button>
              </div>
            ) : (event.participantRoster ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {(event.participantRoster ?? []).map(
                  (attendee: EventParticipantRosterItem) => (
                  <div
                    key={attendee.userId}
                    className="flex items-center gap-2 rounded-full bg-secondary/50 py-1 pl-1 pr-3"
                  >
                    <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full">
                      {attendee.avatarUrl ? (
                        <Image
                          src={attendee.avatarUrl}
                          alt={attendee.displayName}
                          fill
                          sizes="32px"
                          quality={IMAGE_QUALITY_AVATAR}
                          className="object-cover"
                        />
                      ) : (
                        <div className="type-caption type-weight-medium flex h-full w-full items-center justify-center bg-muted">
                          {attendee.displayName
                            .trim()
                            .slice(0, 1)
                            .toUpperCase() || "·"}
                        </div>
                      )}
                    </div>
                    <span className="type-body-sm text-foreground type-weight-medium">
                      {attendee.displayName}
                    </span>
                  </div>
                ))}
              </div>
            ) : showHostManagementChrome ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="type-body text-muted-foreground">No guests yet</p>
                <p className="type-body-sm text-muted-foreground/70">
                  {event.isPublished
                    ? "Share your event to invite guests"
                    : "Publish your event to start inviting guests"}
                </p>
              </div>
            ) : isPublicDemo ? (
              <div className="space-y-4">
                <p className="type-body-sm">
                  Example guest rows — the demo doesn&apos;t sync real RSVPs.
                </p>
                <div className="space-y-2">
                  {[
                    {
                      name: "ami.",
                      initials: null as string | null,
                      avatar:
                        event.hostAvatar?.trim() ||
                        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=80&h=80&q=68",
                    },
                    { name: "Kenna E.", initials: "KE", avatar: null as string | null },
                  ].map((row) => (
                    <div
                      key={row.name}
                      className="flex items-center gap-3 rounded-xl border border-border/40 bg-card/40 p-3"
                    >
                      <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full">
                        {row.avatar ? (
                          <Image
                            src={row.avatar}
                            alt=""
                            fill
                            sizes="40px"
                            quality={IMAGE_QUALITY_AVATAR}
                            className="object-cover"
                          />
                        ) : (
                          <div className="type-body-sm type-weight-medium flex h-full w-full items-center justify-center bg-muted">
                            {row.initials}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="type-body type-weight-medium">{row.name}</p>
                        <p className="type-caption">Going</p>
                      </div>
                      <span className="type-micro type-weight-medium shrink-0 rounded-full bg-success-soft px-2 py-1 text-success">
                        Sample
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="type-body-sm py-8 text-center">
                No guests yet.
              </p>
            )}
          </div>
        )}

        {activeTab === "Settings" && showHostManagementChrome && (
          <div className="space-y-4">
            <EventManagersPanel
              eventId={event.id}
              enabled={!isPublicDemo && showHostManagementChrome}
              onTeamChanged={() => void refreshEventFromApi()}
            />
            <div className="surface-panel">
              <h2 className="type-card-title mb-4">Event Settings</h2>
              <div className="space-y-3">
                <Link 
                  href={`/edit/${event.id}`}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Edit className="w-5 h-5 text-muted-foreground" />
                    <span>
                      {isPastEventByStoredDate(event.date)
                        ? "Manage Memory"
                        : "Edit Event Details"}
                    </span>
                  </div>
                </Link>
                <button 
                  onClick={handleCopyLink}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Copy className="w-5 h-5 text-muted-foreground" />
                    <span>Copy Event Link</span>
                  </div>
                  {linkCopied && <CheckCircle className="w-4 h-4 text-success" />}
                </button>
                {event.visibility === EVENT_VISIBILITY_LINK_ONLY ? (
                  <div className="rounded-xl border border-border/60 bg-secondary/10 px-4 py-3 space-y-3">
                    <p className="type-caption">
                      {event.shareLinkDisabled
                        ? "Anonymous visitors can’t use the share key until you turn link access back on."
                        : "Anyone with this link and share key can view the event without signing in."}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        disabled={shareCtlBusy || Boolean(event.shareLinkDisabled)}
                        onClick={() => {
                          setShareCtlBusy(true)
                          void (async () => {
                            try {
                              await patchEventViaApi(event.id, {
                                shareLinkEnabled: false,
                              })
                              await refreshEventFromApi()
                            } finally {
                              setShareCtlBusy(false)
                            }
                          })()
                        }}
                      >
                        Disable link access
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        disabled={shareCtlBusy || !event.shareLinkDisabled}
                        onClick={() => {
                          setShareCtlBusy(true)
                          void (async () => {
                            try {
                              await patchEventViaApi(event.id, {
                                shareLinkEnabled: true,
                              })
                              await refreshEventFromApi()
                            } finally {
                              setShareCtlBusy(false)
                            }
                          })()
                        }}
                      >
                        Enable link access
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        disabled={shareCtlBusy}
                        onClick={() => {
                          setShareCtlBusy(true)
                          void (async () => {
                            try {
                              await patchEventViaApi(event.id, {
                                regenerateShareAccessToken: true,
                              })
                              await refreshEventFromApi()
                            } finally {
                              setShareCtlBusy(false)
                            }
                          })()
                        }}
                      >
                        New share key
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Visibility */}
            <div className="surface-panel">
              <h2 className="type-card-title mb-4">Visibility</h2>
              <div className="rounded-xl bg-secondary/30 p-4">
                <div className="flex items-start gap-3">
                  {event.isPublished ? (
                    <>
                      <Globe className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                      <div className="min-w-0">
                        <p className="type-body type-weight-medium">Published</p>
                        <p className="type-body-sm text-muted-foreground">
                          Can appear in Story when eligible. Shared links follow your visibility
                          settings.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Lock className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                      <div className="min-w-0">
                        <p className="type-body type-weight-medium">Not published</p>
                        <p className="type-body-sm text-muted-foreground">
                          Not in Story or other publish-driven surfaces. The event stays in your
                          history; you can publish again anytime.
                        </p>
                      </div>
                    </>
                  )}
                </div>
                {event.isPublished && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowUnpublishConfirm(true)}
                    className="mt-3 h-auto justify-start px-0 py-0 text-muted-foreground hover:text-foreground sm:mt-0 sm:ml-auto sm:h-9 sm:px-3 sm:py-2"
                  >
                    Remove from Story
                  </Button>
                )}
              </div>
            </div>

            {/* Host archive — separate from unpublish and delete */}
            <div className="surface-panel">
              <h2 className="type-card-title mb-4">Archive</h2>
              <div className="flex flex-col gap-3 rounded-xl bg-secondary/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="type-body type-weight-medium">
                    {isHostArchived ? "Archived" : "Active in your lists"}
                  </p>
                  <p className="type-body-sm text-muted-foreground">
                    {isHostArchived
                      ? "Kept in your account, but hidden from default browsing and Story."
                      : "Keep it, but remove it from active views and Story without deleting it."}
                  </p>
                </div>
                {isHostArchived ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 rounded-full"
                    disabled={archiveBusy}
                    onClick={() => void handleHostArchiveToggle(false)}
                  >
                    {archiveBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Restore to active lists"
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 rounded-full"
                    onClick={() => setShowArchiveConfirm(true)}
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Archive
                  </Button>
                )}
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-destructive/5 rounded-2xl border border-destructive/20 p-5 sm:p-6">
              <h2 className="type-card-title mb-2 text-destructive">Delete permanently</h2>
              <p className="type-body-sm mb-4 text-muted-foreground">
                Deleting is separate from unpublishing or archiving. This cannot be undone.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDeleteScope("default")
                    setShowDeleteConfirm(true)
                  }}
                  className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete permanently
                </Button>
                {canDeleteSeries ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDeleteScope("series")
                      setShowDeleteConfirm(true)
                    }}
                    className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete series
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </EventDetailShell>
    </main>

      <EventMediaAddModal
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        eventId={event.id}
        onUploaded={handleMediaUploaded}
        canUploadPhotos={event.canUploadPhotos ?? true}
        canUploadVideos={event.canUploadVideos ?? true}
      />

      {/* Unpublish Confirmation Modal */}
      {showUnpublishConfirm && (
        <ModalOverlay
          onClick={() => setShowUnpublishConfirm(false)}
        >
          <ModalContent
            className="animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalBody>
            <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-warning" />
            </div>
            <h3 className="type-dialog-title">Remove from Story?</h3>
            <p className="type-body-sm mb-6 text-center text-muted-foreground">
              This removes the event from Story and other publish-driven surfaces. Nothing is
              deleted — it stays in your history, and you can publish again anytime.
            </p>
            <div className="flex min-w-0 flex-wrap gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowUnpublishConfirm(false)}
                className="min-w-0 flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUnpublish}
                className="min-w-0 flex-1 rounded-full bg-warning text-warning-foreground hover:bg-warning/90"
              >
                Remove from Story
              </Button>
            </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {showArchiveConfirm && (
        <ModalOverlay onClick={() => !archiveBusy && setShowArchiveConfirm(false)}>
          <ModalContent
            className="animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalBody>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/40">
                <Archive className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="type-dialog-title">Archive this event?</h3>
              <p className="type-body-sm mb-6 text-center text-muted-foreground">
                It will be kept, but removed from active views and Story. You can restore it
                whenever you like.
              </p>
              <div className="flex min-w-0 flex-wrap gap-3">
                <Button
                  variant="outline"
                  disabled={archiveBusy}
                  onClick={() => setShowArchiveConfirm(false)}
                  className="min-w-0 flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  disabled={archiveBusy}
                  onClick={() => void handleHostArchiveToggle(true)}
                  className="min-w-0 flex-1 rounded-full"
                >
                  {archiveBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Archive"
                  )}
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && event && (
        <ModalOverlay
          onClick={() => {
            if (!deleteBusy) {
              setShowDeleteConfirm(false)
              setDeleteScope("default")
            }
          }}
        >
          <ModalContent
            className="animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalBody>
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-destructive" />
            </div>
            <h3 className="type-dialog-title">{deleteConfirmTitle}</h3>
            <ul className="type-body-sm mb-6 list-disc space-y-2 pl-5 text-left text-muted-foreground">
              <li>
                {deleteScope === "series"
                  ? "Permanently remove every event in this series, including each occurrence and associated media references."
                  : "Permanently remove this event and its media references from your account and shared surfaces."}
              </li>
              {event.isPublished ? (
                <li>Published links and Story will stop showing it immediately.</li>
              ) : null}
              <li>
                <span className="font-medium text-foreground">This cannot be undone.</span>
              </li>
            </ul>
            <div className="flex min-w-0 flex-wrap gap-3">
              <Button 
                variant="outline" 
                disabled={deleteBusy}
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setDeleteScope("default")
                }}
                className="min-w-0 flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button 
                disabled={deleteBusy}
                onClick={() => void handleDelete()}
                className="min-w-0 flex-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  deleteScope === "series" ? "Delete series" : "Delete permanently"
                )}
              </Button>
            </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}
